<template>
    <div>
        <b-card
            class="article"
            :class="{
                'extended-reading': extended_reading,
                'collapsed': !article.visible
            }"
            no-body
        >
            <template #header>
                <CardID :id="article.id" class="mb-2" type="Article"/>
                <div class="row">
                    <div class="input-group col-5"
                         v-if="unit.custom_article_numbering && include_numbering && page.page != 'Extended Reading'">
                        <div class="input-group-prepend" style="height: 40px; max-height: 40px;">
                            <span class="input-group-text">Custom Article Number: </span>
                        </div>                        
                        <input
                            v-model="article.new_custom_number"
                            class="form-control"
                            :class="{'is-invalid': customNumberConflict(article.custom_number) && article.custom_number}"
                            aria-label="Custom Article Number"
                            style="max-width: 75px; height: 40px; max-height: 40px;"
                            type="number"
                            @change="updateCustomArticleNumber(article.id, article.new_custom_number)"
                            :ref="'article_custom_number_input_'+ article.id"
                            :disabled="!can_update_article || disabled || usedByLockedPublication"
                        >
                    </div>
                    <div class="input-group col-5" v-else-if="include_numbering && page.page != 'Extended Reading'">
                        <div class="input-group-prepend" style="height: 40px; max-height: 40px;">
                            <span class="input-group-text">Article Number: {{ sequentialArticleNumber }}</span>
                        </div>
                    </div>
                    <div class="col-12">
                        <div class="float-right">
                            <div v-if="article">
                                <input
                                    class="form-control mb-1"
                                    :class="{ 'bg-danger text-white': isExpired }"
                                    type="date"
                                    :value="(article.expiration_date || '').slice(0,10)"
                                    @input="onExpirationDateInput"
                                />
                            </div>
                            <b-badge class="mr-2" v-if="hasLinkedArticlePedigrees" variant="warning" pill>
                                Linked Article
                            </b-badge>
                            <b-button
                                v-if="usedByLockedPublication"
                                variant="danger"
                                v-b-tooltip.hover
                                title="A locked publication uses this article."
                                size="sm"
                                @click="showLockedPublications"
                            >
                                <font-awesome-icon :icon="['fas', 'lock']" fixed-width />
                            </b-button>
                        </div>
                    </div>
                </div>
                <div class="row">
                    <div class="col-5 radio-group pt-2" style="height: 40px; max-height: 40px;">
                        <div class="radio-item">
                            <b-form-radio
                                switch
                                @change="updateArticleType('article', $event)"
                                v-model="article.article_type"
                                value="article"
                                :disabled="!can_update_article || disabled || usedByLockedPublication"
                            >
                                Article
                            </b-form-radio>
                        </div>
                        <div class="radio-item">
                            <b-form-radio
                                switch
                                @change="updateArticleType('activity', $event)"
                                v-model="article.article_type"
                                value="activity"
                                :disabled="!can_update_article || disabled || usedByLockedPublication"
                            >
                                Activity
                            </b-form-radio>
                        </div>
                        <div class="radio-item">
                            <b-form-radio
                                switch
                                @change="updateArticleType('walkthrough', $event)"
                                v-model="article.article_type"
                                value="walkthrough"
                                :disabled="!can_update_article || disabled || usedByLockedPublication"
                            >
                                Walkthrough
                            </b-form-radio>
                        </div>
                        <div class="radio-item">
                            <b-form-radio
                                switch
                                @change="updateArticleType('rubric', $event)"
                                v-model="article.article_type"
                                value="rubric"
                                :disabled="!can_update_article || disabled || usedByLockedPublication"
                            >
                                Rubric
                            </b-form-radio>
                        </div>
                        <div class="radio-item">
                            <b-form-radio
                                switch
                                @change="updateArticleType('assessment', $event)"
                                v-model="article.article_type"
                                value="assessment"
                                :disabled="!can_update_article || disabled || usedByLockedPublication"
                            >
                                Assessment
                            </b-form-radio>
                        </div>
                    </div>
                </div>

                <span
                    v-if="customNumberConflict(article.custom_number) && article.custom_number && unit.custom_article_numbering && include_numbering"
                    class="small text-danger"
                >
                    Another article shares the same custom number
                </span>

                <div class="d-flex align-items-center">

                    <div
                        class="mr-3"
                        :class="{'disabled text-muted': article.visible}"
                    >
                        <div v-if="updating_order" class="text-dark mr-1">
                            <b-spinner small label="Loading..."></b-spinner>
                        </div>
                        <b-btn
                            v-if="article_position == 0 && page['page'] == 1"
                            class="btn-order"
                            block
                            disabled
                        >
                            <font-awesome-icon :icon="['fas', 'stop']" fixed-width />
                        </b-btn>
                        <b-btn
                            v-else
                            @click="reorderArticle(article, 'up', article_position)"
                            block
                            class="btn-order"
                            :disabled="!can_update_article || disabled || usedByLockedPublication"
                        >
                            <font-awesome-icon :icon="['fas', 'caret-up']" fixed-width />
                        </b-btn>
                        <b-btn
                            v-if="page_article_length == article_position + 1 && page['page'] == 'Extended Reading'"
                            block
                            class="mt-1 btn-order"
                            disabled
                        >
                            <font-awesome-icon :icon="['fas', 'stop']" fixed-width />
                        </b-btn>
                        <b-btn
                            v-else
                            @click="reorderArticle(article, 'down', article_position)"
                            block
                            class="mt-1 btn-order"
                            :disabled="!can_update_article || disabled || usedByLockedPublication"
                        >
                            <font-awesome-icon :icon="['fas', 'caret-down']" fixed-width />
                        </b-btn>
                    </div>

                    <div class="d-flex align-items-center mr-3 flex-grow-1">
                        <span
                            class="collapse-icon mr-2"
                            :class="{'collapsed': !article.visible, 'disabled': article_body_changes}"
                            :aria-expanded="article.visible ? 'true' : 'false'"
                            @click="toggleVisibility('visible')"
                        >
                            <span class="open">
                                <font-awesome-icon :icon="['fas', 'caret-down']" fixed-width />
                            </span>
                            <span class="closed">
                                <font-awesome-icon :icon="['fas', 'caret-right']" fixed-width />
                            </span>
                        </span>

                        <div class="flex-grow-1 d-flex align-items-center flex-nowrap">
                            <div class="flex-grow-1 mr-3">
                                <Title
                                    v-model="article.title"
                                    :publication="publication"
                                    :unit="unit"
                                    :article="article"
                                    :variant="'light'"
                                    :disabled="se_locked || !can_update_article || disabled || usedByLockedPublication"
                                    @update="updateArticleTitle"
                                />
                            </div>

                            <div class="d-flex flex-column mr-3">

                                <div>
                                    <b-form-checkbox
                                        size="sm"
                                        v-model="article.is_optional"
                                        :checked="article.is_optional"
                                        :disabled="se_locked || !can_update_article || disabled || usedByLockedPublication"
                                        switch
                                        @change="updateArticleByProperty('is_optional', $event)"
                                    >
                                        Optional
                                    </b-form-checkbox>
                                </div>
                                <div>
                                    <b-form-checkbox
                                        size="sm"
                                        v-model="completed"
                                        :checked="completed"
                                        :disabled="se_locked || !can_update_article || disabled || usedByLockedPublication"
                                        class="is-complete-checkbox"
                                        switch
                                        @change="markAsComplete($event)"
                                    >
                                        Complete
                                    </b-form-checkbox>
                                </div>
                                <div>
                                    <b-form-checkbox
                                        size="sm"
                                        v-model="allow_print"
                                        :checked="allow_print"
                                        :disabled="se_locked || !can_update_article || disabled || usedByLockedPublication"
                                        switch
                                        @change="markAsPrintable($event, unit.id, article.id)"
                                    >
                                        Allow Print
                                    </b-form-checkbox>
                                </div>
                                <div>
                                    <b-form-checkbox
                                        size="sm"
                                        v-model="include_numbering"
                                        :checked="include_numbering"
                                        :disabled="se_locked || !can_update_article || disabled || usedByLockedPublication"
                                        switch
                                        @change="toggleIncludeNumbering($event, unit.id, article.id)"
                                    >
                                        Include Numbering
                                    </b-form-checkbox>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="ml-auto d-flex flex-column">
                        <b-btn
                            :variant="copy_to_clipboard_btn_variant"
                            :class="{'active': copying_to_clipboard}"
                            content="Copy Article Link"
                            v-tippy="{placement: 'top'}"
                            @click="copyToClipboard(unit.id, article.id)"
                            :disabled="copying_to_clipboard"
                            class="my-2"
                            size="sm"
                        >
                            <span v-if="copying_to_clipboard">
                                <font-awesome-icon :icon="['fas', 'check']" size="sm" fixed-width />
                            </span>
                            <span v-else>
                                <font-awesome-icon :icon="['fas', 'link']" size="sm" fixed-width />
                            </span>
                        </b-btn>
                        <b-dropdown
                            size="sm"
                            variant="secondary"
                            :disabled="se_locked"
                            class="my-2"
                            dropleft
                        >
                            <template #button-content>
                                <font-awesome-icon :icon="['fad', 'cog']" />
                            </template>

                            <b-dropdown-item
                                @click="updateArticle"
                                :disabled="!can_update_article || disabled || usedByLockedPublication"
                            >
                                <font-awesome-icon :icon="['fad', 'check-square']" class="text-primary" fixed-width />
                                Save <span v-if="article.is_activity">Activity</span><span v-else>Article</span>
                            </b-dropdown-item>

                            <!-- added back into trunk 4/16/2024 -->
                            <b-dropdown-item
                                @click="startArticleDuplication"
                                :disabled="!can_duplicate_article"
                            >
                                <font-awesome-icon :icon="['fad', 'clone']" class="text-info" fixed-width />
                                Duplicate
                            </b-dropdown-item>

                            <b-dropdown-item
                                v-if="hasLinkedArticlePedigrees"
                                @click="convertToDuplicate"
                                :disabled="!can_duplicate_article || !can_unlink_article || disabled"
                            >
                                <font-awesome-icon :icon="['fad', 'unlink']" class="text-warning" fixed-width />
                                Convert to Duplicate
                            </b-dropdown-item>

                            <b-dropdown-item
                                @click="unlinkArticleFromPage"
                                :disabled="!can_unlink_article || disabled"
                            >
                                <font-awesome-icon :icon="['fad', 'trash']" class="text-danger" fixed-width />
                                Delete
                            </b-dropdown-item>
                            <b-dropdown-item
                                @click="importArticle"
                                :disabled="!can_import_article || disabled || usedByLockedPublication"
                            >
                                <font-awesome-icon :icon="['fad', 'share']" class="text-success" fixed-width />
                                Import 3.0
                            </b-dropdown-item>
                        </b-dropdown>
                    </div>
                </div>
            </template>

            <b-collapse
                v-model="article.visible"
                class="border-top"
                :class="article.visible ? null : 'collapsed'"
            >
                <div v-if="article.visible">
                    <div v-if="article.loading" class="p-3">
                        <content-placeholders>
                            <content-placeholders-text :lines="3" />
                        </content-placeholders>
                    </div>
                    <div v-else>
                        <div v-if="article_data">
                            <Assessment
                                v-if="article.is_assessment"
                                :publication="publication"
                                :unit="unit"
                                :article="article"
                                :initial_article_data="article_data"
                                :disabled="disabled"
                                :available_standards="available_standards"
                                :state_grade="state_grade"
                            />
                            <b-list-group v-if="!article.is_assessment" v-show="hasAnyStudentElements" class="student-edition" flush>
                                <b-list-group-item
                                    :id="'unit' + unit.id + 'article' + article.id + 'body'"
                                    class="article-body rounded-0"
                                    v-if="article_type == 'article' && isElementVisible(element_visibility, 'article_body')"
                                >
                                    <div class="h6 d-flex align-items-center">
                                        <div>
                                            Article Body
                                            <span v-if="article_data.language && article_data.language.length"
                                                  :content="'Designated Spanish version of ' + article_data.language[0].id + ' - ' + article_data.language[0].title"
                                                  v-tippy="{placement: 'top'}"
                                            >
                                                <flag iso="es" />
                                            </span>
                                        </div>
                                        <div class="ml-auto d-flex">
                                            <template v-if="article_data.status_locked">
                                                <div class="alert alert-danger pt-1 pb-0 pl-2 pr-1 mb-0"
                                                     style="font-size: .875rem">
                                                    This article has been locked.
                                                    <button
                                                        class="btn btn-sm mb-1 text-danger"
                                                        @click="unlockArticleBody"
                                                        :disabled="!can_update_article || disabled || usedByLockedPublication || se_locked"
                                                    >
                                                        <font-awesome-icon :icon="['fad', 'unlock']" fixed-width />
                                                        Unlock
                                                    </button>
                                                </div>
                                                <div class="mx-2">
                                                    <b-btn-group>
                                                        <b-btn
                                                            variant="light"
                                                            @click="show_changelog_modal = true"
                                                            content="Show Changelog"
                                                            v-tippy="{placement: 'bottom'}"
                                                        >
                                                            <font-awesome-icon
                                                                :icon="['fas', 'history']"
                                                                fixed-width
                                                            />
                                                        </b-btn>
                                                        <!--
                                                            For locked articles, the save button doesn't actually need to do anything.
                                                            However, we show it for the purposes of identifying change/copy log state.
                                                        -->
                                                        <b-btn
                                                            variant="light"
                                                            :class="{
                                                                'article-log-state': logUpdateType !== 'body_copied',
                                                            }"
                                                        >
                                                            <font-awesome-icon :icon="['fad', 'save']" fixed-width />
                                                        </b-btn>
                                                        <CopyToClipboardButton
                                                            class="p-2"
                                                            :class="{
                                                                'article-log-state': logUpdateType === 'body_copied',
                                                            }"
                                                            :value="article_data.body"
                                                            :as-html="true"
                                                            size="sm"
                                                            @clipboard-copy="logUpdate"
                                                            strip-tags
                                                        />
                                                    </b-btn-group>
                                                </div>
                                            </template>
                                            <template v-else>
                                                <b-btn
                                                    size="sm"
                                                    variant="outline-danger"
                                                    @click="lockArticleBody"
                                                    :disabled="!can_update_article || disabled || usedByLockedPublication || se_locked"
                                                >
                                                    <font-awesome-icon :icon="['fad', 'lock']" fixed-width />
                                                    Lock Article Body
                                                </b-btn>
                                            </template>
                                        </div>
                                        <div>
                                            <b-btn size="sm" variant="outline-info"
                                                   @click="show_article_preview_modal = true" class="ml-2">
                                                <font-awesome-icon :icon="['fad', 'apple-alt']" fixed-width />
                                                SWO Preview
                                            </b-btn>
                                        </div>
                                    </div>

                                    <div class="d-flex align-items-center">
                                        <div v-if="article_data.status_locked" class="mr-3">
                                            <font-awesome-icon :icon="['fad', 'lock']" class="text-danger" fixed-width
                                                               content="Article Body Locked" v-tippy />
                                        </div>
                                        <div class="flex-grow-1">
                                            <TipTap
                                                ref="article-body"
                                                v-model="article_data.body"
                                                :menu_items="[
                                                    'bold',
                                                    'italic',
                                                    'underline',
                                                    'bullet_list',
                                                    'ordered_list',
                                                    'table',
                                                    'image',
                                                    'edit_html',
                                                ]"
                                                :min_height="true"
                                                :is_article_body="true"
                                                :article="article"
                                                :readonly="readonly || !can_update_article || disabled || usedByLockedPublication"
                                                @input="setContent('body', $event)"
                                                @blur="updateArticleByProperty('body', article_data.body)"
                                                @save="updateArticleByProperty('body', article_data.body)"
                                                @update="updateArticleByProperty('body', article_data.body)"
                                                @update_tiptap_table="updateArticleFromTiptap($event)"
                                                @clipboard-copy="logUpdate"
                                                @show-changelog-modal="show_changelog_modal = true"
                                                strip-tags
                                            />
                                        </div>
                                    </div>

                                    <div class="d-flex mt-1">
                                        <div class="mr-3">
                                            <small class="text-muted">
                                                <div class="d-flex align-items-center mb-1">
                                                    <div class="mr-1">Lexile:</div>
                                                    <div class="d-flex align-items-center">

                                                        <div v-if="recalculating_lexile_score" class="text-dark mr-1">
                                                            <b-spinner small label="Loading..."></b-spinner>
                                                        </div>

                                                        <div v-else class="d-flex align-items-center mr-1">
                                                            <div>
                                                                <span
                                                                    v-if="article_data.lexile_api_score == null">0</span>
                                                                <span v-else>{{ article_data.lexile_api_score }}</span>
                                                            </div>
                                                        </div>

                                                        <div class="mr-1">
                                                            <b-link
                                                                v-if="!disabled && !usedByLockedPublication"
                                                                href="#"
                                                                :class="{'disabled': recalculating_lexile_score}"
                                                                :disabled="recalculating_lexile_score"
                                                                @click.prevent="recalculateLexileScore"
                                                            >
                                                                <span
                                                                    v-if="article_data.lexile_api_score !== null">re-</span>calculate
                                                            </b-link>
                                                        </div>

                                                    </div>

                                                    <div class="d-flex align-items-center">
                                                        <b-form-input
                                                            ref="lexile-certified-score"
                                                            style="font-size: 80%"
                                                            size="sm"
                                                            type="number"
                                                            placeholder="Certified Score"
                                                            :disabled="se_locked || !lexile_certified_score_editable || disabled || usedByLockedPublication"
                                                            v-model="article_data.lexile_certified_score"
                                                            @blur="saveLexileCertifiedScore(article_data.lexile_certified_score)"
                                                            @keydown.enter="saveLexileCertifiedScore(article_data.lexile_certified_score)"
                                                        />
                                                    </div>
                                                </div>
                                            </small>
                                        </div>
                                        <div class="d-flex align-items-center mb-1 ml-auto">
                                            <div class="d-flex align-items-center mr-1">
                                                <small>
                                                    <div v-if="sw_ids.updated">
                                                        <a :href="sw_ids.url" target="_blank">
                                                            Last exported {{ sw_ids.updated }}
                                                        </a>
                                                    </div>
                                                    <div v-else>This article has not been exported.</div>
                                                </small>
                                            </div>
                                            <div class="d-flex align-items-center">
                                                <span
                                                    v-if="article_data.updated_by_user"
                                                    :content="'Last updated ' + article_data.updated + ' by ' + article_data.updated_by_user.first_name + ' ' + article_data.updated_by_user.last_name"
                                                    v-tippy="{placement: 'top'}"
                                                    class="text-primary"
                                                >
                                                    <font-awesome-icon :icon="['fad', 'pencil']" fixed-width />
                                                </span>

                                            </div>
                                        </div>
                                    </div>
                                </b-list-group-item>

                                <QuestionLayout
                                    v-if="article.is_activity && isElementVisible(element_visibility, 'article_activity_layout')"
                                    :section-id="'unit' + unit.id + 'article' + article.id + 'activity_layout'"
                                    :publication="publication"
                                    :unit="unit"
                                    :article="article"
                                    :article-data="article_data"
                                    :disabled="disabled"
                                    :available-standards="available_standards"
                                    :state-grade="state_grade"
                                    :can-update-article="can_update_article"
                                    :used-by-locked-publication="usedByLockedPublication"
                                    header-title="Activity Layout"
                                    add-button-label="Add Item"
                                    @refresh-article="refreshQuestionLayout"
                                >
                                    <template #header-actions>
                                        <b-btn-group class="ml-2">
                                            <b-btn
                                                variant="light"
                                                @click="show_changelog_modal = true"
                                                content="Show Changelog"
                                                v-tippy="{placement: 'bottom'}"
                                            >
                                                <font-awesome-icon
                                                    :icon="['fas', 'history']"
                                                    fixed-width
                                                />
                                            </b-btn>
                                            <b-btn
                                                variant="light"
                                                :class="{
                                                    'article-log-state': logUpdateType !== 'body_copied',
                                                }"
                                            >
                                                <font-awesome-icon :icon="['fad', 'save']" fixed-width />
                                            </b-btn>
                                            <CopyToClipboardButton
                                                class="p-2"
                                                :class="{
                                                    'article-log-state': logUpdateType === 'body_copied',
                                                }"
                                                :value="async () => await questionsAsPlaintext()"
                                                :as-html="false"
                                                size="sm"
                                                @clipboard-copy="logUpdate"
                                            />
                                        </b-btn-group>

                                        <b-btn
                                            size="sm"
                                            variant="outline-info"
                                            @click="show_article_preview_modal = true"
                                            class="float-right ml-2"
                                        >
                                            <font-awesome-icon :icon="['fad', 'apple-alt']" fixed-width />
                                            SWO Preview
                                        </b-btn>
                                    </template>
                                </QuestionLayout>

                                <b-list-group-item
                                    :id="'unit' + unit.id + 'article' + article.id + 'walkthrough'"
                                    class="article-body rounded-0"
                                    v-if="article.is_walkthrough && isElementVisible(element_visibility, 'article_walkthrough')"
                                >
                                    <Walkthrough
                                        :publication="publication"
                                        :unit="unit"
                                        :article="article"
                                        :disabled="disabled || !can_update_article || usedByLockedPublication"
                                    />
                                </b-list-group-item>

                                <b-list-group-item
                                    :id="'unit' + unit.id + 'article' + article.id + 'rubric_items'"
                                    class="article-body rounded-0"
                                    v-if="article_type == 'rubric' && isElementVisible(element_visibility, 'article_rubric_items')"
                                >
                                    <div class="h6 d-flex align-items-center justify-content-end">
                                        <div>
                                            <b-btn size="sm" variant="outline-info"
                                                   @click="show_article_preview_modal = true" class="ml-2">
                                                <font-awesome-icon :icon="['fad', 'apple-alt']" fixed-width />
                                                SWO Preview
                                            </b-btn>
                                        </div>
                                    </div>
                                    <div class="h6 d-flex align-items-center">
                                        <div>Rubric Items</div>
                                    </div>
                                    <b-btn
                                        size="sm"
                                        variant="outline-info"
                                        @click="newArticleQuestion(article)"
                                        class="ml-2"
                                        :disabled="!can_update_article || !can_add_question || disabled || usedByLockedPublication"
                                    >
                                        <font-awesome-icon :icon="['far', 'plus']" fixed-width />
                                        Add Item
                                    </b-btn>
                                    <div
                                        v-for="(question, index) in article_data.questions"
                                        :key="index"
                                        class="mt-4"
                                    >
                                        <div v-if="question.type == 'media_block' || question.type == 'text_block'"
                                             class="d-flex align-items-center">
                                            <div
                                                class="pr-3 flex-column d-flex align-self-stretch question-sort-controls"
                                            >
                                                <b-btn
                                                    size="sm"
                                                    variant="outline-primary"
                                                    class='mt-3'
                                                    :disabled="index <= 0 || !can_update_article || disabled || usedByLockedPublication"
                                                    @click='questionSortUp(question)'
                                                >
                                                    <font-awesome-icon :icon="['fas', 'caret-up']" fixed-width />
                                                </b-btn>
                                                <b-btn
                                                    size="sm"
                                                    variant="outline-primary"
                                                    class='mb-3'
                                                    :disabled="index >= article_data.questions.length-1 || !can_update_article || disabled || usedByLockedPublication"
                                                    @click='questionSortDown(question)'
                                                >
                                                    <font-awesome-icon :icon="['fas', 'caret-down']" fixed-width />
                                                </b-btn>
                                            </div>
                                            <div class="flex-grow-1">
                                                <div v-if="question.loading">
                                                    <b-spinner small label="Loading..."></b-spinner>
                                                </div>
                                                <div v-else>
                                                    <Question
                                                        :publication="publication"
                                                        :unit="unit"
                                                        :article="article"
                                                        :question="question"
                                                        :available_standards="available_standards"
                                                        :state_grade="state_grade"
                                                        @update="updateArticleQuestion"
                                                        @unlinkQuestion="removeArticleQuestion"
                                                        :disabled="disabled || usedByLockedPublication"
                                                    />
                                                    <div v-if="question.narration_loading">
                                                        <content-placeholders>
                                                            <content-placeholders-text :lines="3" />
                                                        </content-placeholders>
                                                    </div>
                                                    <div
                                                        v-else-if="question.type == 'text_block' && question.narration && question.narration.url">
                                                        <div class="ml-3 h6">
                                                            Narration
                                                        </div>
                                                        <b-card no-body>
                                                            <b-card-body>
                                                                <div class="d-flex align-items-center">
                                                                    <div>
                                                                        <audio ref="narration-audio" class="d-flex mb-0"
                                                                               controls>
                                                                            <source :src="question.narration.url"
                                                                                    type="audio/mpeg">
                                                                            Your browser does not support the audio
                                                                            element.
                                                                        </audio>
                                                                    </div>
                                                                    <div class="d-flex align-items-center ml-auto">
                                                                        <div v-if="question.narration.alignment_status"
                                                                             class="mr-3">
                                                                            <span
                                                                                class="badge"
                                                                                :class="narration_badge(question.narration)">
                                                                                {{
                                                                                    narration_badge_text(question.narration)
                                                                                }}
                                                                            </span>
                                                                            <span
                                                                                class="badge badge-light border ml-1"
                                                                                :title="'Aligned by ' + narration_driver_label(question.narration)">
                                                                                {{
                                                                                    narration_driver_label(question.narration)
                                                                                }}
                                                                            </span>
                                                                        </div>
                                                                        <div>
                                                                            <b-dropdown
                                                                                size="sm"
                                                                                variant="secondary"
                                                                                :disabled="se_locked"
                                                                                dropleft
                                                                            >
                                                                                <template #button-content>
                                                                                    <font-awesome-icon
                                                                                        :icon="['fad', 'cog']" />
                                                                                </template>
                                                                                <b-dropdown-item
                                                                                    v-if="question.narration.returned && !question.narration.approved"
                                                                                    @click="approveNarration(question.narration)">
                                                                                    <font-awesome-icon
                                                                                        :icon="['fad', 'check']"
                                                                                        class="text-success"
                                                                                        fixed-width />
                                                                                    Approve
                                                                                </b-dropdown-item>
                                                                                <b-dropdown-item
                                                                                    @click="reparseNarration(question.narration)">
                                                                                    <font-awesome-icon
                                                                                        :icon="['fad', 'sync-alt']"
                                                                                        class="text-warning"
                                                                                        fixed-width />
                                                                                    Re-parse
                                                                                </b-dropdown-item>
                                                                                <b-dropdown-item
                                                                                    v-for="engine in alignment_driver_options"
                                                                                    :key="'realign-question-' + engine.value"
                                                                                    @click="realignNarration(question.narration, engine.value, question)">
                                                                                    <font-awesome-icon
                                                                                        :icon="['fad', 'sync-alt']"
                                                                                        class="text-warning"
                                                                                        fixed-width />
                                                                                    Re-align with {{ engine.text }}
                                                                                </b-dropdown-item>
                                                                                <b-dropdown-item
                                                                                    @click="testNarrationAudio(question.narration)">
                                                                                    <font-awesome-icon
                                                                                        :icon="['fad', 'waveform-path']"
                                                                                        class="text-secondary"
                                                                                        fixed-width />
                                                                                    Test Synced Audio
                                                                                </b-dropdown-item>
                                                                                <b-dropdown-item
                                                                                    @click="deleteNarration(question.narration, question)">
                                                                                    <font-awesome-icon
                                                                                        :icon="['fad', 'trash']"
                                                                                        class="text-danger"
                                                                                        fixed-width />
                                                                                    Delete
                                                                                </b-dropdown-item>
                                                                            </b-dropdown>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </b-card-body>
                                                        </b-card>
                                                    </div>
                                                    <div v-else-if="question.type == 'text_block'">
                                                        <div v-if="uploading_file">
                                                            <b-spinner label="Loading..."></b-spinner>
                                                        </div>
                                                        <div v-else>
                                                            <b-form-group
                                                                label="Alignment engine"
                                                                label-size="sm"
                                                                class="mb-2"
                                                            >
                                                                <b-form-select
                                                                    size="sm"
                                                                    v-model="alignment_driver"
                                                                    :options="alignment_driver_options"
                                                                    :disabled="se_locked || !can_update_article || disabled || usedByLockedPublication"
                                                                />
                                                            </b-form-group>
                                                            <b-form-file
                                                                v-model="narration_file"
                                                                accept="audio/*"
                                                                browse-text="Upload File"
                                                                :disabled="se_locked || !can_update_article || disabled || usedByLockedPublication"
                                                                @input="uploadNarration($event, question)"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </b-list-group-item>

                                <b-list-group-item v-if="article_type == 'rubric'" class="rounded-0"
                                                   :id="'unit' + unit.id + 'article' + article.id + 'rubric_type'">
                                    <b-form-group>
                                        <label>Rubric Type</label>
                                        <b-form-select
                                            v-model="rubric_type"
                                            :options="possible_rubric_types"
                                            @change="handleRubricTypeChange"
                                            :disabled="!can_update_article || disabled || usedByLockedPublication"
                                        ></b-form-select>
                                    </b-form-group>

                                    <template v-if="rubric_type == 'Single Point Rubric'">
                                        <div class="h3">Single Point</div>

                                        <div>
                                            <div class="d-flex justify-content-between">
                                                <button
                                                    @click="addRubricRow"
                                                    class="btn btn-success mr-3 mb-3"
                                                    :disabled="!can_update_article || disabled || usedByLockedPublication"
                                                >
                                                    Add Row
                                                </button>

                                                <b-btn
                                                    :variant="saving_rubric ? 'success' : 'light'"
                                                    :class="{'active': saving_rubric}"
                                                    class="mb-3"
                                                    content="Save Rubric"
                                                    v-tippy="{placement: 'top'}"
                                                    @click="handleSaveButton"
                                                    :disabled="saving_rubric || !can_update_article || disabled || usedByLockedPublication"
                                                >
                                                    <span v-if="saving_rubric">
                                                        <font-awesome-icon :icon="['fas', 'check']" fixed-width />
                                                    </span>
                                                    <span v-else>
                                                        <font-awesome-icon :icon="['fad', 'save']" fixed-width />
                                                    </span>
                                                </b-btn>
                                            </div>


                                            <table class="single-table">
                                                <tr v-for="(row, index) in tableData" :key="index"
                                                    class="d-flex justify-content-between">
                                                    <template>
                                                        <td v-if="row.index == 0 && !updating_rubric"
                                                            v-for="cell in row.cells" :key="cell.index"
                                                            class="rubric-cell rubric-row-1">
                                                            {{ cell.text }}
                                                        </td>
                                                        <td v-else class="single-rubric-cell">
                                                            <div>
                                                                <button
                                                                    v-if="cell.index == 0"
                                                                    @click="remove_criteria_row(index)"
                                                                    class="btn btn-sm btn-danger single-rubric-row-delete"
                                                                    :disabled="!can_update_article || disabled || usedByLockedPublication"
                                                                >
                                                                    <font-awesome-icon :icon="['fad', 'trash']" />
                                                                </button>
                                                                <textarea
                                                                    :key="cell.index"
                                                                    v-model="cell.text"
                                                                    class="single-text-area"
                                                                    @change="saveRubric"
                                                                    :disabled="!can_update_article || disabled || usedByLockedPublication"
                                                                ></textarea>
                                                            </div>
                                                        </td>
                                                        <td v-if="row.index == 0 && !updating_rubric"
                                                            class="w-25 text-center rubric-row-1">
                                                            Rubric Score
                                                        </td>
                                                        <td v-else
                                                            class="w-25 d-flex justify-content-center align-items-center">
                                                            <b-form-input
                                                                :id="`row-${index}-score`"
                                                                v-model="row.score"
                                                                type="number"
                                                                min="1"
                                                                class="rubric-score"
                                                                @blur="saveRubric"
                                                                placeholder="1"
                                                                :disabled="!can_update_article || disabled || usedByLockedPublication"
                                                            />
                                                        </td>
                                                    </template>
                                                </tr>

                                            </table>
                                        </div>
                                    </template>


                                    <template v-else>
                                        <div class="h3">Multiple point rubric</div>
                                        <div>

                                            <button
                                                @click="addRubricRow"
                                                class="btn btn-success mr-3 mb-3"
                                                :disabled="!can_update_article || disabled || usedByLockedPublication"
                                            >
                                                Add Row
                                            </button>
                                            <button
                                                @click="addRubricColumn"
                                                class="btn btn-success mr-3 mb-3"
                                                :disabled="!can_update_article || disabled || usedByLockedPublication"
                                            >
                                                Add Column
                                            </button>

                                            <table>
                                                <tr v-for="(row, index) in tableData" :key="index">
                                                    <template>
                                                        <td v-if="row.index == 0 && !updating_rubric"
                                                            v-for="cell in row.cells"
                                                            :key="`row-${row.index}cell-${cell.index}`"
                                                            class="rubric-cell  rubric-row-1">
                                                            {{ cell.text }}
                                                            <button
                                                                v-if="row.index == 0 && cell.index != 0"
                                                                @click="remove_criteria_cell(cell.index)"
                                                                class="btn btn-sm btn-danger"
                                                                :disabled="!can_update_article || disabled || usedByLockedPublication"
                                                            >
                                                                <font-awesome-icon :icon="['fad', 'trash']" />
                                                            </button>
                                                        </td>
                                                        <td v-else class="rubric-cell">
                                                            <div class="row rubric-textarea-container">
                                                                <button
                                                                    v-if="cell.index == 0 && cell.index != row.cells.length - 1"
                                                                    @click="remove_criteria_row(index)"
                                                                    class="col-3 btn btn-sm btn-danger rubric-row-delete"
                                                                    :disabled="!can_update_article || disabled || usedByLockedPublication"
                                                                >
                                                                    <font-awesome-icon :icon="['fad', 'trash']" />
                                                                </button>
                                                                <textarea
                                                                    :key="cell.index"
                                                                    v-model="cell.text"
                                                                    :class="cell.index == 0 ? 'col-9' : null"
                                                                    @change="saveRubric"
                                                                    :disabled="!can_update_article || disabled || usedByLockedPublication"
                                                                ></textarea>
                                                            </div>

                                                        </td>
                                                    </template>

                                                </tr>

                                            </table>
                                        </div>
                                    </template>

                                </b-list-group-item>

                                <b-list-group-item v-if="!article.is_walkthrough && isElementVisible(element_visibility, 'article_designer_notes')" class="rounded-0"
                                                   :id="'unit' + unit.id + 'article' + article.id + 'designer_notes'">
                                    <div class="h6">Designer Notes</div>
                                    <TipTap
                                        v-model="article_data.designer_notes"
                                        :menu_type="wysiwyg_menu_type"
                                        @blur="updateArticleByProperty('designer_notes', article_data.designer_notes)"
                                        :readonly="!can_update_article || disabled || usedByLockedPublication"
                                    />
                                </b-list-group-item>

                                <b-list-group-item v-if="article.is_activity && isElementVisible(element_visibility, 'article_activity_answer_key')" class="article-answer-key"
                                                   :id="'unit' + unit.id + 'article' + article.id + 'activity_answer_key'">
                                    <div class="h6">
                                        Activity Answer Key
                                    </div>

                                    <TipTap
                                        v-model="article_data.activity_answer_key"
                                        :readonly="se_locked || !can_update_article || disabled || usedByLockedPublication"
                                        @input="setContent('activity_answer_key', $event)"
                                        @blur="updateArticleByProperty('activity_answer_key', article_data.activity_answer_key)"
                                    />
                                </b-list-group-item>

                                <b-list-group-item
                                    v-if="!article.is_walkthrough && isElementVisible(element_visibility, 'article_narration')"
                                    class="article-narration"
                                    :id="'unit' + unit.id + 'article' + article.id + 'narration'"
                                >
                                    <div class="h6">Narration</div>
                                    <div v-if="publicationIsPreK()"
                                         class="flex-grow-1 d-flex align-items-center flex-nowrap">
                                        <div class="d-flex flex-column mr-3">
                                            <div>
                                                <b-form-checkbox
                                                    size="sm"
                                                    v-model="hide_missing_audio_message"
                                                    :checked="hide_missing_audio_message"
                                                    :disabled="se_locked || !can_update_article || disabled || usedByLockedPublication"
                                                    class="is-complete-checkbox"
                                                    switch
                                                    @change="toggleHideMissingAudioMessage($event)"
                                                >
                                                    Hide No Audio Warning On SWO
                                                </b-form-checkbox>
                                            </div>
                                        </div>
                                    </div>
                                    <div v-if="article.narration_loading">
                                        <content-placeholders>
                                            <content-placeholders-text :lines="3" />
                                        </content-placeholders>
                                    </div>
                                    <div v-else class="mt-2">
                                        <div v-if="article.narration && article.narration.url">
                                            <b-card no-body>
                                                <b-card-body>
                                                    <div class="d-flex align-items-center">
                                                        <div>
                                                            <audio ref="narration-audio" class="d-flex mb-0" controls>
                                                                <source :src="secureUrl(article.narration.url)"
                                                                        type="audio/mpeg">
                                                                Your browser does not support the audio element.
                                                            </audio>
                                                        </div>
                                                        <div class="d-flex align-items-center ml-auto">
                                                            <div v-if="article.narration.status" class="mr-3">
                                                                <span
                                                                    class="badge"
                                                                    :class="narration_badge(article.narration)">
                                                                    {{ narration_badge_text(article.narration) }}
                                                                </span>
                                                                <span
                                                                    class="badge badge-light border ml-1"
                                                                    :title="'Aligned by ' + narration_driver_label(article.narration)">
                                                                    {{ narration_driver_label(article.narration) }}
                                                                </span>
                                                            </div>
                                                            <div>
                                                                <b-dropdown
                                                                    size="sm"
                                                                    variant="secondary"
                                                                    :disabled="se_locked"
                                                                    dropleft
                                                                >
                                                                    <template #button-content>
                                                                        <font-awesome-icon :icon="['fad', 'cog']" />
                                                                    </template>
                                                                    <b-dropdown-item
                                                                        v-if="article.narration.returned && !article.narration.approved"
                                                                        @click="approveNarration(article.narration)"
                                                                        :disabled="!can_update_article || disabled || usedByLockedPublication"
                                                                    >
                                                                        <font-awesome-icon :icon="['fad', 'check']"
                                                                                           class="text-success"
                                                                                           fixed-width />
                                                                        Approve
                                                                    </b-dropdown-item>
                                                                    <b-dropdown-item
                                                                        @click="reparseNarration(article.narration)"
                                                                        :disabled="!can_update_article || disabled || usedByLockedPublication"
                                                                    >
                                                                        <font-awesome-icon :icon="['fad', 'sync-alt']"
                                                                                           class="text-warning"
                                                                                           fixed-width />
                                                                        Re-parse
                                                                    </b-dropdown-item>
                                                                    <b-dropdown-item
                                                                        v-for="engine in alignment_driver_options"
                                                                        :key="'realign-article-' + engine.value"
                                                                        @click="realignNarration(article.narration, engine.value)"
                                                                        :disabled="!can_update_article || disabled || usedByLockedPublication"
                                                                    >
                                                                        <font-awesome-icon :icon="['fad', 'sync-alt']"
                                                                                           class="text-warning"
                                                                                           fixed-width />
                                                                        Re-align with {{ engine.text }}
                                                                    </b-dropdown-item>
                                                                    <b-dropdown-item
                                                                        @click="testNarrationAudio(article.narration)"
                                                                        :disabled="!can_update_article || disabled || usedByLockedPublication"
                                                                    >
                                                                        <font-awesome-icon
                                                                            :icon="['fad', 'waveform-path']"
                                                                            class="text-secondary" fixed-width />
                                                                        Test Synced Audio
                                                                    </b-dropdown-item>
                                                                    <b-dropdown-item
                                                                        @click="deleteNarration(article.narration)"
                                                                        :disabled="!can_update_article || disabled || usedByLockedPublication"
                                                                    >
                                                                        <font-awesome-icon :icon="['fad', 'trash']"
                                                                                           class="text-danger"
                                                                                           fixed-width />
                                                                        Delete
                                                                    </b-dropdown-item>
                                                                </b-dropdown>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </b-card-body>
                                            </b-card>
                                        </div>
                                        <div v-else>
                                            <div v-if="uploading_file">
                                                <b-spinner label="Loading..."></b-spinner>
                                            </div>
                                            <div v-else>
                                                <b-form-group
                                                    label="Alignment engine"
                                                    label-size="sm"
                                                    class="mb-2"
                                                >
                                                    <b-form-select
                                                        size="sm"
                                                        v-model="alignment_driver"
                                                        :options="alignment_driver_options"
                                                        :disabled="se_locked || !can_update_article || disabled || usedByLockedPublication"
                                                    />
                                                </b-form-group>
                                                <b-form-file
                                                    v-model="narration_file"
                                                    accept="audio/*"
                                                    browse-text="Upload File"
                                                    :disabled="se_locked || !can_update_article || disabled || usedByLockedPublication"
                                                    @input="uploadNarration($event, null)"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </b-list-group-item>

                                <b-list-group-item
                                    :id="'unit' + unit.id + 'article' + article.id + 'panels'"
                                    v-if="!article.is_walkthrough && isElementVisible(element_visibility, 'article_panels')"
                                >
                                    <div class="h6">Panels</div>
                                    <panel-section
                                        :panels="article_data.panels"
                                        :article="article"
                                        :publication="publication"
                                        :disabled="disabled || usedByLockedPublication"
                                    />
                                </b-list-group-item>

                                <b-list-group-item
                                    :id="'unit' + unit.id + 'article' + article.id + 'vocabulary'"
                                    class="article-vocabulary"
                                    v-if="!article.is_walkthrough && isElementVisible(element_visibility, 'article_vocabulary')"
                                >
                                    <div class="h6 mb-0">
                                        <span
                                            class="collapse-icon text-dark mr-1"
                                            :class="article.vocabulary_visible ? null : 'collapsed'"
                                            :aria-expanded="article.vocabulary_visible ? 'true' : 'false'"
                                            @click="toggleVisibility('vocabulary_visible')"
                                        >
                                            <span class="open">
                                                <font-awesome-icon :icon="['fas', 'caret-down']" fixed-width />
                                            </span>
                                            <span class="closed">
                                                <font-awesome-icon :icon="['fas', 'caret-right']" fixed-width />
                                            </span>
                                            Vocabulary
                                        </span>
                                    </div>

                                    <b-collapse
                                        v-model="article.vocabulary_visible"
                                        :class="article.vocabulary_visible ? null : 'collapsed'"
                                    >
                                        <div v-if="article.vocabulary_visible" class="mt-2">
                                            <div v-if="article.vocabulary_loading">
                                                <content-placeholders>
                                                    <content-placeholders-text :lines="3" />
                                                </content-placeholders>
                                            </div>
                                            <div v-else>
                                                <div v-if="vocabulary_terms && vocabulary_terms.length">
                                                    <div v-for="(vocabulary_term, index) in vocabulary_terms"
                                                         :key="index">
                                                        <vocabulary-term
                                                            :publication="publication"
                                                            :unit="unit"
                                                            :article="article"
                                                            :vocabulary_term="vocabulary_term"
                                                            :vocabulary_terms="vocabulary_terms"
                                                            @update="getVocabularyTerms"
                                                            @unlink="getVocabularyTerms"
                                                            :readonly="disabled || usedByLockedPublication"
                                                        />
                                                    </div>
                                                </div>

                                                <b-alert
                                                    variant="danger"
                                                    :show="!vocabulary_terms || vocabulary_terms && !vocabulary_terms.length"
                                                    class="my-2"
                                                >
                                                    0 Vocabulary Terms added
                                                </b-alert>

                                                <div class="d-flex">
                                                    <div class="ml-auto">
                                                        <b-btn
                                                            size="sm"
                                                            variant="success"
                                                            :disabled="se_locked || !can_update_article || disabled || usedByLockedPublication"
                                                            @click.prevent="newVocabularyTerm"
                                                        >
                                                            <font-awesome-icon :icon="['fad', 'plus-square']" />
                                                            Create Vocabulary Term
                                                        </b-btn>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </b-collapse>
                                </b-list-group-item>

                                <b-list-group-item
                                    class="article-questions"
                                    v-if="article_type == 'article' && isElementVisible(element_visibility, 'article_questions')"
                                    :class="{'rounded-0': hasAnyTeacherElements}"
                                    :id="'unit' + unit.id + 'article' + article.id + 'article_questions'"
                                >
                                    <div class="h6 m-0">Article Questions

                                        <span style="position: absolute; right: 5%;">
                                            <a :href="'/questionList/article/'+article.id" target="_blank"
                                               class="pull-right"><font-awesome-icon :icon="['fad', 'clipboard']"
                                                                                     style="color: grey;" fixed-width /></a>
                                        </span>
                                    </div>
                                    <div
                                        v-for="(question, index) in article_data.questions"
                                        :key="index"
                                        class="mt-2"
                                    >
                                        <div class="d-flex align-items-center">
                                            <div
                                                class="pr-3 flex-column d-flex align-self-stretch question-sort-controls"
                                            >
                                                <b-btn
                                                    size="sm"
                                                    variant="outline-primary"
                                                    class="mt-3"
                                                    :disabled="index <= 0 || !can_update_article || disabled || usedByLockedPublication"
                                                    @click="questionSortUp(question)"
                                                >
                                                    <font-awesome-icon :icon="['fas', 'caret-up']" fixed-width />
                                                </b-btn>
                                                <b-btn
                                                    size="sm"
                                                    variant="outline-primary"
                                                    class="mb-3"
                                                    :disabled="index >= article_data.questions.length - 1 || !can_update_article || disabled || usedByLockedPublication"
                                                    @click="questionSortDown(question)"
                                                >
                                                    <font-awesome-icon :icon="['fas', 'caret-down']" fixed-width />
                                                </b-btn>
                                            </div>
                                            <div class="mr-3 ml-3">
                                                {{ index + 1 }}.
                                            </div>
                                            <div class="flex-grow-1">
                                                <div v-if="question.loading">
                                                    <b-spinner small label="Loading..."></b-spinner>
                                                </div>
                                                <div v-else>
                                                    <Question
                                                        :publication="publication"
                                                        :unit="unit"
                                                        :article="article"
                                                        :question="question"
                                                        :available_standards="available_standards"
                                                        :state_grade="state_grade"
                                                        @update="updateArticleQuestion"
                                                        @unlinkQuestion="removeArticleQuestion"
                                                        :disabled="disabled || usedByLockedPublication"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div v-for="num in numQuestionButtons" :key="num" class="mt-2">
                                        <div class="d-flex align-items-center">
                                            <div class="pr-3 question-sort-spacer" />
                                            <div class="mr-3 ml-3">
                                                {{ num + article_data.questions.length }}.
                                            </div>
                                            <div class="flex-grow-1">
                                                <b-btn
                                                    variant="outline-success"
                                                    :disabled="se_locked || !can_update_article || !can_add_question || disabled || usedByLockedPublication"
                                                    @click="newArticleQuestion(article)"
                                                    block
                                                >
                                                    <font-awesome-icon :icon="['fad', 'plus-square']" />
                                                </b-btn>
                                            </div>
                                        </div>
                                    </div>
                                </b-list-group-item>
                            </b-list-group>

                            <b-list-group v-if="!article.is_assessment" v-show="hasAnyTeacherElements" class="teacher-edition"
                                          :class="{'border-top': hasAnyTeacherElements}" flush>

                                <b-list-group-item
                                    v-if="!article.is_walkthrough && isElementVisible(element_visibility, 'article_materials_calculator')"
                                    :id="`unit${unit.id}article${article.id}materials_calculator`"
                                >
                                    <MaterialsCalculator
                                        :article_id="article.id"
                                        :unit_id="unit.id"
                                        :publication_id="publication.id"
                                        :disabled="!can_update_article || disabled || usedByLockedPublication"
                                    />
                                </b-list-group-item>

                                <b-list-group-item
                                    v-if="!article.is_walkthrough && isElementVisible(element_visibility, 'article_plan')"
                                    class="article-lesson-plan-tasks"
                                    :id="'unit' + unit.id + 'article' + article.id + 'lesson_plan'"
                                >
                                    <LessonPlan
                                        :article="article"
                                        :publication="publication"
                                        :disabled="!can_update_article || disabled || usedByLockedPublication"
                                    />
                                </b-list-group-item>

                                <b-list-group-item
                                    v-if="!article.is_walkthrough && isElementVisible(element_visibility, 'article_printables')"
                                    :id="'unit' + unit.id + 'article' + article.id + 'graphic-organizers'"
                                    class="article-graphic-organizer"
                                >
                                    <div class="h6 mb-0">
                                        <span
                                            class="collapse-icon text-dark mr-1"
                                            :class="article.worksheets_visible ? null : 'collapsed'"
                                            :aria-expanded="article.worksheets_visible ? 'true' : 'false'"
                                            @click="toggleVisibility('worksheets_visible')"
                                        >
                                            <span class="open">
                                                <font-awesome-icon :icon="['fas', 'caret-down']" fixed-width />
                                            </span>
                                            <span class="closed">
                                                <font-awesome-icon :icon="['fas', 'caret-right']" fixed-width />
                                            </span>
                                            Printables
                                        </span>
                                    </div>

                                    <b-collapse
                                        v-model="article.worksheets_visible"
                                        :class="article.worksheets_visible ? null : 'collapsed'"
                                    >
                                        <WorksheetsList
                                            :article="article"
                                            :unit="unit"
                                            :publication="publication"
                                            :disabled="!can_update_article || disabled || usedByLockedPublication"
                                        />
                                    </b-collapse>

                                </b-list-group-item>

                                <b-list-group-item
                                    v-if="!article.is_walkthrough && isElementVisible(element_visibility, 'article_related_media')"
                                    :id="'unit' + unit.id + 'article' + article.id + 'related-media'"
                                    class="article-related-media"
                                >
                                    <related-media
                                        :unit="unit"
                                        :publication="publication"
                                        :article="article"
                                        :disabled="!can_update_article || disabled || usedByLockedPublication"
                                    />
                                </b-list-group-item>

                            </b-list-group>

                            <b-list-group v-if="!article.is_assessment && !article.is_walkthrough" flush class="border-top student-edition">
                                <b-list-group-item class="rounded-0"
                                                   v-if="isElementVisible(element_visibility, 'article_citations')"
                                                   :id="'unit' + unit.id + 'article' + article.id + 'citations'">
                                    <div class="h6">Citations</div>

                                    <TipTap
                                        v-model="article_data.citations"
                                        :menu_items="['bold', 'italic', 'underline']"
                                        @input="setContent('citations', $event)"
                                        @blur="updateArticleByProperty('citations', article_data.citations)"
                                        :readonly="!can_update_article || disabled || usedByLockedPublication"
                                    />
                                </b-list-group-item>

                                <b-list-group-item v-if="isElementVisible(element_visibility, 'article_tags')" class="article-tags rounded-0"
                                                   :id="'unit' + unit.id + 'article' + article.id + 'tags'">
                                    <div class="h6">Tags</div>

                                    <!-- Intentionally leaving out disabled/locked pub checks for tags, as we want these to be allowed to be changed for a locked pub. -->
                                    <tag-multiselect
                                        v-model="article_data.tags"
                                        :options="tags"
                                        @select="addTag"
                                        @remove="removeTag"
                                        :disabled="!can_update_article"
                                    />
                                </b-list-group-item>
                            </b-list-group>

                            <b-list-group v-if="!article.is_walkthrough" class="border-top" flush>
                                <b-list-group-item class="article-pedigree-chart"
                                                   v-if="isElementVisible(element_visibility, 'article_pedigree')"
                                                   :id="'unit' + unit.id + 'article' + article.id + 'pedigree_chart'">
                                    <div class="h6 mb-0">
                                        <span
                                            class="collapse-icon text-dark mr-1"
                                            :class="article.predigree_chart_visible ? null : 'collapsed'"
                                            :aria-expanded="article.predigree_chart_visible ? 'true' : 'false'"
                                            @click="toggleVisibility('predigree_chart_visible')"
                                        >
                                            <span class="open">
                                                <font-awesome-icon :icon="['fas', 'caret-down']" fixed-width />
                                            </span>
                                            <span class="closed">
                                                <font-awesome-icon :icon="['fas', 'caret-right']" fixed-width />
                                            </span>
                                            Pedigree
                                        </span>
                                    </div>

                                    <b-collapse
                                        v-model="article.predigree_chart_visible"
                                        :class="article.predigree_chart_visible ? null : 'collapsed'"
                                    >
                                        <PedigreeChart
                                            :article="article"
                                            :unit="unit"
                                            :publication="publication"
                                        />
                                    </b-collapse>
                                </b-list-group-item>
                            </b-list-group>

                        </div>
                    </div>
                </div>
            </b-collapse>
        </b-card>
        <!-- </drag> -->
        <vocabulary-term-modal
            v-model="show_vocabulary_term_modal"
            :article="article"
            :vocabulary_terms="vocabulary_terms"
            @create="createVocabularyTerm"
            @update="updateVocabularyTerm"
            @hide="show_vocabulary_term_modal = false"
        />

        <narration-audio-sync-modal
            v-model="show_narration_audio_sync_modal"
            :article="article"
            :narration="currentNarration"
            @approve="approveNarration(currentNarration)"
            @hide="show_narration_audio_sync_modal = false"
        />

        <question-modal
            v-model="show_question_modal"
            :unit="unit"
            :article="article"
            :question="question"
            :available_standards="available_standards"
            :state_grade="state_grade"
            @create="createArticleQuestion"
            @hide="show_question_modal = false"
        />

        <duplicate-article-modal
            v-model="show_duplicate_article_modal"
            :unit="unit"
            @duplicate="duplicateArticle"
            @hide="show_duplicate_article_modal = false"
        />

        <copy-te-modal
            v-model="show_copy_te_modal"
            @copy="copyTe"
            @hide="show_copy_te_modal = false"
        />

        <article-preview-modal
            v-model="show_article_preview_modal"
            @hide="show_article_preview_modal = false"
            :article="article_data"
            :include_numbering="include_numbering"
            :seq_article_number="sequentialArticleNumber"
            :custom_article_number="article.custom_number"
            :is_k3="is_k3"
            :tableData="tableData"
            :walkthroughMedia="[]"
            :rubric_type="rubric_type"
        />

        <change-log-modal
            :visible="show_changelog_modal"
            :article-id="article.id"
            :unit-id="unit.id"
            :publication-id="publication.id"
            :title="article.title"
            :current-article="article_data"
            @update:visible="show_changelog_modal = $event"
        />

        <panel-validation-failed-modal
            v-model="show_panel_validation_failed_modal"
            :untitled_modals="untitled_modals"
            @hide="show_panel_validation_failed_modal = false"
        />
    </div>
</template>

<style lang="scss">
.is-complete-checkbox {
    .custom-control-input:checked ~ .custom-control-label::before {
        border-color: #27AE60;
        background-color: #27AE60;
    }
}
</style>

<style lang="scss" scoped>
@import '@/assets/css/_colors';

.btn-order {
    color: #000;
    background-color: lightgrey;
    border-radius: 3px;
    padding: 0;
}

.question-sort-controls {
    justify-content: space-between;
    border-right: 1px solid #999;
}

.question-sort-spacer {
    width: 46px;
    border-right: 1px solid transparent;
}

.student-edition {
    .list-group-item {
        border-left: 3px solid #B2EBF2;
    }
}

.teacher-edition {
    .list-group-item {
        border-left: 3px solid #FFAB91;
    }
}

.card {
    &.collapsed {
        .card-header {
            border-radius: .25rem;
        }
    }

    .card-header {
        border-bottom: 0;
    }

    .list-group {
        .list-group-item {
            &:first-child {
                // border-radius: 0;
            }

            .list-group {
                .list-group-item {
                    // border-radius: inherit;
                }
            }
        }
    }
}

.radio-group {
    display: flex;
    justify-content: space-between;
}

.radio-item {
    flex: 1;
    margin-right: 10px;
}

.rubric-textarea-container {
    width: max-content;
    min-height: 100px;
}

.rubric-row-delete {
    max-width: 35px;
    max-height: 35px;
    margin-right: 3px;
}

.rubric-score {
    width: 70px;
}

.criteria-input {
    // width: 80%;
    // overflow: auto;
}

.point-requirement {
    // overflow: auto;
}

.rubric-cell {
    text-align: center;
    flex-grow: 1;
}

.add_criteria_cell {
    text-align: center;
    border: none;
}

.single-table {
    width: 70%
}

.single-rubric-cell {
    position: relative;
    height: 100px;
    min-height: 100px;
    flex-grow: 1;
}

.single-rubric-row-delete {
    position: absolute;
    top: 5px;
    max-width: 35px;
    max-height: 35px;
    min-height: 35px;
    margin-right: 3px;
}

.single-text-area {
    position: absolute;
    top: 5px;
    left: 50px;
    height: 90px;
    max-height: 90px;
    width: 75%;
}

.article-log-state {
    background-color: rgb(52, 152, 219);
    color: white;
}

</style>

<script>
import TagMultiselect from '@/components/TagMultiselect'
import {mapState} from 'vuex'
import { isElementVisible } from '@/common/elementVisibility'

import VueSticky from 'vue-sticky'

import CardID from '@/components/CardID'
import Title from '@/components/Title'

import TipTap from '@/components/tiptap/TipTap'
import ArticlePreviewModal from '@/components/article/SWOPreviewModal'
import Assessment from '@/components/article/Assessment'
import QuestionLayout from '@/components/article/QuestionLayout'

import VocabularyTerm from '@/components/article/VocabularyTerm'
import VocabularyTermModal from '@/components/modals/article/VocabularyTermModal'

import NarrationAudioSyncModal from '@/components/modals/article/NarrationAudioSyncModal'

import QuestionContent from '@/components/questions/Index'
import Question from '@/components/article/Question'
import QuestionModal from '@/components/modals/questions/QuestionModal'

import PedigreeChart from '@/components/article/PedigreeChart'
import MaterialsCalculator from '@/components/article/MaterialsCalculator'
import LessonPlan from '@/components/article/LessonPlan'

import DuplicateArticleModal from '@/components/modals/article/DuplicateArticleModal'

import CopyTeModal from '@/components/modals/unit/CopyTeModal'

import RelatedMedia from '@/components/article/related_media/Index'

import moment from 'moment'

import colors from '@/common/colors'

import PanelSection from '@/components/panels/PanelSection'

import article_question_types from '@/common/article_question_types'
import eventBus from '@/common/event-bus'
import copy from 'copy-html-to-clipboard'

import draggable from 'vuedraggable'
import CopyToClipboardButton from '@/components/CopyToClipboardButton'
import {exportQuestions} from '@/common/questionsExporter/exportQuestions'
import Walkthrough from '@/components/article/Walkthrough'
import ChangeLogModal from '@/components/modals/article/ChangeLogModal'
import WorksheetsList from '@/components/worksheet/WorksheetsList'
import PanelValidationFailedModal from '@/components/modals/publication/PanelValidationFailedModal'

export default {
    data: () => ({
        vocabulary_terms: [],
        related_media: [],
        colors: colors,
        pedigree_chart: [],

        article_data: {},
        currentNarration: {},
        narration: {},

        question: {},

        article_body_changes: false,

        recalculating_lexile_score: false,

        show_vocabulary_term_modal: false,

        narration_file: null,
        uploading_file: false,
        show_narration_audio_sync_modal: false,

        // Alignment engine used for the next narration upload. Keep these in
        // step with alignment_drivers/alignment_driver in config/narration.php.
        alignment_driver: 'cadence',
        alignment_driver_options: [
            { value: 'cadence', text: 'Cadence' },
            { value: 'threeplay', text: '3Play' },
        ],

        related_media_visible: false,
        related_media_loading: false,

        show_question_modal: false,

        show_duplicate_article_modal: false,
        show_article_preview_modal: false,
        show_copy_te_modal: false,
        show_changelog_modal: false,
        show_panel_validation_failed_modal: false,
        untitled_modals: [],

        numQuestionButtons: 0,

        article_question_types,

        article_teacher_editions_visible: false,
        updating_order: false,
        article_type: null,

        walkthrough_media_loading: false,
        tableData: [],
        rubric_type: 'Multi-Point Rubric',
        possible_rubric_types: ['Single Point Rubric', 'Multi-Point Rubric'],
        updating_rubric: false,
        rubric_id: null,
        rubric_updated: false,
        saving_rubric: false,
        copying_to_clipboard: false,

        selected_article_pedigree_relationship_type: 'all',

        updatingByProperty: {},
        saved_body: null,
    }),
    props: [
        'publication_id',
        'publication',
        'unit_group',
        'unit',
        'article',
        'page',
        'extended_reading',
        'article_position',
        'page_article_length',
        'disabled',
        'available_standards',
        'state_grade',
    ],
    directives: {
        'sticky': VueSticky,
    },
    components: {
        TagMultiselect,
        Assessment,
        QuestionLayout,
        WorksheetsList,
        Walkthrough,
        CopyToClipboardButton,
        MaterialsCalculator,
        LessonPlan,
        PedigreeChart,
        CardID,
        Title,
        TipTap,
        ArticlePreviewModal,
        VocabularyTerm,
        VocabularyTermModal,
        NarrationAudioSyncModal,
        Question,
        QuestionContent,
        QuestionModal,
        DuplicateArticleModal,
        CopyTeModal,
        ChangeLogModal,
        RelatedMedia,
        PanelSection,
        draggable,
        PanelValidationFailedModal,
    },
    computed: {
        hasLinkedArticlePedigrees() {
            const pedigrees = Array.isArray(this.article && this.article.pedigrees)
                ? this.article.pedigrees
                : []
            const pedigreeFilter = (pedigree) =>
                pedigree.relationship_type === 'Linked Article' && pedigree.unit_id !== this.unit.id
            return pedigrees.filter(pedigreeFilter).length !== 0
        },
        hasAnyStudentElements() {
            const keys = [
                'article_body',
                'article_activity_layout',
                'article_walkthrough',
                'article_rubric_items',
                'article_designer_notes',
                'article_activity_answer_key',
                'article_narration',
                'article_panels',
                'article_vocabulary',
                'article_questions',
                'article_citations',
                'article_tags',
            ]
            return keys.some(key => this.isElementVisible(key))
        },
        hasAnyTeacherElements() {
            const keys = [
                'article_materials_calculator',
                'article_plan',
                'article_printables',
                'article_related_media',
            ]
            return keys.some(key => this.isElementVisible(key))
        },
        sw_ids() {
            var sw_ids = {};
            _.each(this.article_data.sw_ids, ids => {
                if (this.publication_id == ids.publication_id && this.unit.id == ids.unit_id && this.article.id == ids.article_id) {
                    ids.updated = moment(ids.updated).format("MM-DD-YYYY");
                    ids.url = '//app.studiesweekly.com/online/publications/' + ids.publication_sw_id + '/units/' + ids.unit_sw_id + '/articles/' + ids.article_sw_id
                    sw_ids = ids;
                }
            })
            return sw_ids;
        },
        is_k3() {
            if (this.publication.state_grades.length) {
                let sg = this.publication.state_grades
                if (sg[0].grade == 0 || sg[0].grade == 1 || sg[0].grade == 2 || sg[0].grade == 3) return true;
                return false;
            } else {
                if (this.publication.format == 'k_1' || this.publication.format == '2_3') return true;
            }
            return false;
        },
        ...mapState({
            user: state => state.user.user,
            wysiwyg_menu_type: state => state.settings.wysiwyg_menu_type,
            element_visibility: state => state.settings.element_visibility,
            lexile_certified_score_editable: state => state.settings.lexile_certified_score_editable,
            tags: state => state.tags.tags,
        }),
        can_update_article() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'update_article')
        },
        can_duplicate_article() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'duplicate_article')
        },
        can_unlink_article() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'unlink_article')
        },
        can_import_article() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'import_article')
        },
        can_add_question() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'add_question')
        },
        sequentialArticleNumber() {
            let sequentialNumber = null
            let includeNumberingAdjustment = 1
            _.forIn(this.unit.articles, (unit_article, index) => {
                if (!unit_article.include_numbering) includeNumberingAdjustment -= 1
                if (unit_article.id == this.article.id) {
                    sequentialNumber = parseInt(index, 10) + includeNumberingAdjustment
                }
            })
            return sequentialNumber
        },
        completed: {
            get() {
                return this.article.completed_at ? true : false
            },
            set(completed) {
                this.$set(this.article, 'completed_at', completed)
            }
        },
        allow_print: {
            get() {
                return this.article.allow_print ? true : false
            },
            set(allow_print) {
                this.$set(this.article, 'allow_print', allow_print)
            }
        },
        include_numbering: {
            get() {
                return this.article.include_numbering ? true : false
            },
            set(include_numbering) {
                this.$set(this.article, 'include_numbering', include_numbering)
            }
        },
        hide_missing_audio_message: {
            get() {
                return this.article.hide_missing_audio_message ? true : false
            },
            set(hide_missing_audio_message) {
                this.$set(this.article, 'hide_missing_audio_message', hide_missing_audio_message)
            }
        },
        se_locked() {
            let se_locked

            if (
                this.publication.se_locked ||
                this.unit.se_locked
            ) {
                se_locked = true
            } else {
                se_locked = false
            }

            return se_locked
        },
        readonly() {
            let readonly = false

            if (
                this.article_data.status_locked ||
                this.se_locked
            ) {
                readonly = true
            }

            return readonly
        },
        copy_to_clipboard_btn_variant() {
            let variant = "secondary"

            if (this.copying_to_clipboard) {
                variant = 'success'
            }

            return variant
        },
        usedByLockedPublication() {
            const pedigrees = this.getLinkedAndLockedPedigrees()

            return pedigrees.length !== 0
        },
        logUpdateType() {
            if (this.article) {
                return this.$store.getters.latestLogTypeByArticleId(this.article.id)
            }

            return null
        },
        can_view_deprecated_fields() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'view_deprecated_fields');
        },
        isExpired() {
            const expirationDate = this.article && this.article.expiration_date;
            if (!expirationDate) {
                return false;
            }
            const [ymd] = String(expirationDate).split(' ');
            if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
                 return false;
            }
            const endOfDay = new Date(`${ymd}T23:59:59`);

            return !isNaN(endOfDay) && Date.now() > endOfDay.getTime();
        }
    },
    watch: {
        'article.visible': {
            handler(visible) {
                if (visible) {
                    this.getArticle()
                    this.getNarration()
                }
            }
        },
        'article.vocabulary_visible': {
            handler(visible) {
                if (visible) {
                    this.getVocabularyTerms()
                }
            }
        },
        'unit.articles': {
            handler(visible) {
                if (visible) {
                    this.$set(this.article, 'new_custom_number', this.article.custom_number)
                }
            }
        }
    },
    methods: {
        isElementVisible,
        secureUrl(url) {
            return url.replace("http:", "https:")
        },
        narration_badge(narration) {
            if (narration.approved) {
                return 'badge-success'
            }
            if (narration.returned) {
                return 'badge-primary'
            }
            if (narration.failed) {
                return 'badge-danger'
            }
            if (narration.alignment_status === 'not_submitted') {
                return 'badge-danger'
            }
            if (['in_progress', 'in_process', 'delivered', 'complete'].includes(narration.alignment_status)) {
                return 'badge-primary'
            }
            return 'badge-danger'
        },
        narration_badge_text(narration) {
            if (narration.approved) {
                return "Audio QC'd"
            }
            if (narration.returned) {
                return 'Audio Synced'
            }
            if (narration.failed) {
                return 'Audio Failed'
            }
            if (narration.alignment_status === 'not_submitted') {
                return 'Not Processed'
            }
            if (['in_progress', 'in_process'].includes(narration.alignment_status)) {
                return 'Audio In Progress'
            }
            return 'Audio Failed'
        },
        // Which engine produced this narration, so two runs of the same audio
        // can be told apart. Narrations predating driver selection are 3Play.
        narration_driver_label(narration) {
            const labels = { cadence: 'Cadence', threeplay: '3Play' }
            const driver = narration.alignment_driver || 'threeplay'

            return labels[driver] || driver
        },

        async questionSortUp(question) {
            let index = this.article_data.questions.indexOf(question)
            if (index <= 0) {
                return
            }

            const previousQuestion = this.article_data.questions[index - 1]

            try {
                this.$set(this.article_data.questions, index, previousQuestion)
                this.$set(this.article_data.questions, index - 1, question)
                await this.axios.post(`/api/articles/reorder_question/${this.article_data.id}`, {
                    question_id: question.id,
                    old_order_number: index + 1,
                    new_order_number: index,
                    publication_id: this.publication.id,
                    unit_id: this.unit.id,
                })
                await this.$store.dispatch(
                    'getLatestLogType',
                    {
                        article_id: this.article.id,
                        unit_id: this.unit.id,
                        publication_id: this.publication.id,
                    }
                )
                this.getArticle()
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to move question: ${e.message}`,
                    },
                    time: moment().toISOString(),
                })
            }
        },
        async questionSortDown(question) {
            let index = this.article_data.questions.indexOf(question)
            if (index >= this.article_data.questions.length - 1) {
                return
            }

            const nextQuestion = this.article_data.questions[index + 1]

            try {
                this.$set(this.article_data.questions, index, nextQuestion)
                this.$set(this.article_data.questions, index + 1, question)
                await this.axios.post(`/api/articles/reorder_question/${this.article_data.id}`, {
                    question_id: question.id,
                    old_order_number: index + 1,
                    new_order_number: index + 2,
                    publication_id: this.publication.id,
                    unit_id: this.unit.id,
                })
                await this.$store.dispatch(
                    'getLatestLogType',
                    {
                        article_id: this.article.id,
                        unit_id: this.unit.id,
                        publication_id: this.publication.id,
                    }
                )
                this.getArticle()
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to move question: ${e.message}`,
                    },
                    time: moment().toISOString(),
                })
            }
        },
        async importArticle() {
            try {
                const validationResponse = await this.axios.get(
                    '/api/articles/validate_import/' + this.article.id + '/' + this.unit.id
                )

                if (!validationResponse.data.valid) {
                    this.untitled_modals = validationResponse.data.untitled_panels || []
                    this.show_panel_validation_failed_modal = this.untitled_modals.length > 0
                    return
                }

                const confirmResponse = await this.$swal({
                    html: 'Are you sure you want to import this article to SWO?',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Import article',
                    confirmButtonColor: '#27AE60',
                    cancelButtonText: 'Cancel',
                    reverseButtons: true
                })

                if (!confirmResponse || !confirmResponse.value) {
                    return
                }

                const response = await this.axios.post(
                    '/api/articles/publish3/' + this.article.id + '/' + this.unit.id + '/' + this.publication_id
                )

                if (!response) return

            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to import article: ${e.message}`,
                    },
                    time: moment().toISOString(),
                })
            }
        },
        getArticle() {
            this.$set(this.article, 'loading', true)

            return this.$store.dispatch('getArticles', this.article.article_id)
                .then(response => {
                    if (response.data) {
                        this.article_data = response.data[0]
                        this.saved_body = response.data[0].body
                    }

                    this.numQuestionButtons = (3 - this.article_data.questions.length) < 0 ? 0 : 3 - this.article_data.questions.length;

                    this.$set(this.article, 'loading', false)
                })
                .catch(error => {
                    if (error) {
                        if (
                            error.response &&
                            error.response.status &&
                            error.response.status == 401
                        ) {
                            this.$set(this.article, 'visible', false)
                        }

                        this.$set(this.article, 'loading', false)
                    }
                })
        },
        async updateArticle(article) {
            try {
                this.$Progress.start()
                await this.axios.post('/api/articles/update/' + this.article.article_id, {
                    publication_id: this.publication.id,
                    unit_id: this.unit.id,
                    article,
                })
                this.$Progress.finish()
            } catch (e) {
                this.$Progress.fail()
            }
        },
        async logUpdate() {
            await this.$store.dispatch(
                'logArticleUpdate',
                {
                    article_id: this.article.id,
                    unit_id: this.unit.id,
                    publication_id: this.publication.id,
                    update_type: 'body_copied',
                }
            )
        },
        async checkStandardArticleQuestions() {
            const response = await this.axios.get(`/api/articles/questions/${this.article.id}`)
            if (response.data) {
                for (const question of response.data) {
                    if (question.type === 'article_assessment') {
                        continue;
                    }

                    if (!this.article_question_types.includes(question.type)) {
                        this.$swal(`
                            This item cannot be switched to an article as it contains question types that are disallowed in an article.
                            Articles may only contain multiple choice questions.
                        `);
                        return false
                    }
                }
            }

            return true
        },
        async updateArticleByProperty(key, value) {
            if (!this.can_update_article) {
                return
            }

            // Prevents firing multiple updates for the same field.
            // Due to the way blurring works, a user editing an article's body and
            // then hitting the save button will trigger TWO calls to this method.
            if (this.updatingByProperty[key] === true) {
                return
            }

            try {
                this.updatingByProperty[key] = true

                if (key === 'body') {
                    const hadNonMediaTextChange = this.hasNonMediaBodyChange(this.saved_body, value)
                    const narration = this.article_data.narration || this.article.narration

                    this.$Progress.start()
                    const response = await this.axios.post('/api/articles/update/' + this.article.article_id, {
                        publication_id: this.publication.id,
                        unit_id: this.unit.id,
                        [key]: value
                    })

                    this.saved_body = value

                    if (response.data) {
                        this.$set(this.article_data, key, response.data[key])
                    }

                    await this.$store.dispatch(
                        'getLatestLogType',
                        {
                            article_id: this.article.id,
                            unit_id: this.unit.id,
                            publication_id: this.publication.id,
                        }
                    )

                    const narrationAfterSave = this.article_data.narration || this.article.narration || narration
                    if (hadNonMediaTextChange && narrationAfterSave && narrationAfterSave.id) {
                        const result = await this.$swal({
                            html: 'This article body was updated. This article has an audio narration. Would you like to delete the audio?',
                            showCancelButton: true,
                            confirmButtonText: 'Delete Audio',
                            cancelButtonText: 'Keep Audio',
                            cancelButtonColor: '#0d6efd',
                            cancelButtonClass: 'text-white',
                            focusCancel: true,
                            reverseButtons: true
                        })

                        if (result.value) {
                            try {
                                await this.axios.post('/api/articles/delete_narration/' + narrationAfterSave.id)
                                this.$set(this.article, 'narration', {})
                                this.$set(this.article_data, 'narration', {})
                            } catch (e) {
                                await this.$swal({
                                    icon: 'error',
                                    html: 'The article body was saved, but deleting the audio failed.'
                                })
                            }
                        }
                    }

                    this.$Progress.finish()
                    return
                }

                this.$Progress.start()
                await this.axios.post('/api/articles/update/' + this.article.article_id, {
                    publication_id: this.publication.id,
                    unit_id: this.unit.id,
                    [key]: value
                })
                if (key === 'title') {
                    await this.$store.dispatch(
                        'getLatestLogType',
                        {
                            article_id: this.article.id,
                            unit_id: this.unit.id,
                            publication_id: this.publication.id,
                        }
                    )
                }
                this.$Progress.finish()
            } catch (e) {
                this.$Progress.fail()
            } finally {
                delete this.updatingByProperty[key]
            }
        },
        hasNonMediaBodyChange(previousBody, nextBody) {
            return this.extractNonMediaBodyText(previousBody) !== this.extractNonMediaBodyText(nextBody)
        },
        extractNonMediaBodyText(html) {
            if (!html) {
                return ''
            }

            const documentFragment = new DOMParser().parseFromString(html, 'text/html')

            return (documentFragment.body.textContent || '')
                .replace(/\s+/g, ' ')
                .trim()
        },
        async updateArticleTitle(title) {
            await this.updateArticleByProperty('title', title)
            this.$set(this.article, 'title', title)
        },
        async markAsComplete(event) {
            try {
                await this.axios.post('/api/articles/update/' + this.article.article_id, {
                    publication_id: this.publication.id,
                    unit_id: this.unit.id,
                    completed: event
                })
            } catch (e) {
                this.completed = !this.completed
            }
        },
        markAsPrintable(event, unit_id, article_id) {
            let allow_print = event ? 1 : 0;
            this.axios.post('/api/units/update_printable/' + unit_id + '/' + article_id + '/' + allow_print, {})
                .then(response => {

                })
                .catch(error => {
                    if (error) {
                        this.allow_print = !this.allow_print
                    }
                })
        },
        async toggleIncludeNumbering(event, unit_id, article_id) {
            let include_numbering = event ? 1 : 0;

            try {
                await this.axios.post(`/api/units/update_include_numbering/${unit_id}/${article_id}/${include_numbering}`)
            } catch (e) {
                this.include_numbering = !this.include_numbering
            }
        },
        async lockArticleBody() {
            this.$set(this.article_data, 'status_locked', true)

            await this.updateArticleByProperty('status_locked', true)
        },
        async unlockArticleBody() {
            const result = await this.$swal({
                html: 'Are you sure you want to unlock the body for this article?',
                showCancelButton: true,
                confirmButtonText: 'Unlock Article Body',
                confirmButtonColor: '#E67E22',
                cancelButtonText: 'Cancel',
                reverseButtons: true
            })
            if (result.value) {
                this.$set(this.article_data, 'status_locked', false)
                await this.updateArticleByProperty('status_locked', false)
            }
        },
        startArticleDuplication() {
            this.show_duplicate_article_modal = true
        },
        duplicateArticle(new_publication, new_unit, page) {
            this.axios.post('/api/articles/duplicate/' + this.article.article_id, {
                old_publication_id: this.publication.id,
                new_publication_id: new_publication.id,
                old_unit_id: this.unit.id,
                new_unit_id: new_unit.id,
                page: page
            })
                .then(() => {
                    this.show_duplicate_article_modal = false

                    this.$emit('duplicated')

                    this.$store.dispatch('getArticles', this.article.article_id)
                })
        },
        copyTe() {
            this.show_copy_te_modal = false
        },
        async unlinkArticleFromPage() {
            const title = this.article.title ? `<strong>${this.article.title}</strong>` : 'this article'
            const page = this.page.page ? ` from page <strong>${this.page.page}</strong>` : ''
            const message = `Are you sure you want to delete ${title}${page}?`

            const result = await this.$swal({
                html: message,
                showCancelButton: true,
                confirmButtonText: 'Delete Article',
                cancelButtonText: 'Cancel',
                reverseButtons: true
            })
            if (result.value) {
                this.$emit('unlink', this.article)
            }
        },
        async convertToDuplicate() {
            const message = `
                Are you sure you want to convert this linked article to a duplicate?
                <div class="alert alert-danger mt-2 mx-1 px-1">
                    <p>This will change the Article ID and Question IDs.</p>
                    <strong>Student progress may be lost mid-year!</strong>
                </div>
            `
            const result = await this.$swal({
                html: message,
                showCancelButton: true,
                confirmButtonText: 'Convert',
                cancelButtonText: 'Cancel',
                confirmButtonColor: '#E67E22',
                reverseButtons: true,
                customClass: 'swal-wider',
            })
            if (!result.value) {
                return
            }

            try {
                await this.axios.post('/api/articles/linked-duplicate', {
                    'article_id': this.article.id,
                    'unit_id': this.unit.id,
                    'publication_id': this.publication.id,
                })
                this.$emit('duplicated')

                await this.$store.dispatch('getArticles', this.article.article_id)
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to duplicate linked article: ${e.message}`,
                    },
                    time: moment().toISOString(),
                })
            }
        },
        recalculateLexileScore() {
            this.recalculating_lexile_score = true

            this.axios.post('/api/articles/update_lexile_score/' + this.article.article_id)
                .then(response => {
                    if (response.data) {
                        this.$set(this.article_data, 'lexile_api_score', response.data.lexile_api_score)
                    }

                    this.recalculating_lexile_score = false
                })
                .catch(error => {
                    if (error) {
                        this.recalculating_lexile_score = false
                    }
                })
        },
        saveLexileCertifiedScore(score) {
            if (score) {
                this.axios.post('/api/articles/save_lexile_certified_score/' + this.article.article_id, {
                    score: score
                })
                    .then(() => {
                        if (this.$refs['lexile-certified-score']) {
                            this.$refs['lexile-certified-score'].blur()
                        }
                    })
            }
        },
        getVocabularyTerms() {
            this.$set(this.article, 'vocabulary_loading', true)

            this.axios.get('/api/articles/vocabulary/' + this.article.article_id)
                .then(response => {
                    if (response.data) {
                        this.vocabulary_terms = _.orderBy(response.data, 'term')
                    }

                    this.$set(this.article, 'vocabulary_loading', false)
                })
                .catch(error => {
                    if (error) {
                        if (
                            error.response &&
                            error.response.status &&
                            error.response.status == 401
                        ) {
                            this.$set(this.unit, 'vocabulary_visible', false)
                        }

                        this.$set(this.article, 'vocabulary_loading', false)
                    }
                })
        },
        getUnitVocabularyTerms() {
            return new Promise((resolve, reject) => {
                this.axios.get('/api/units/vocabulary/' + this.unit.id)
                    .then(response => {
                        let vocabulary = []

                        if (response.data) {
                            vocabulary = response.data
                            vocabulary = _.orderBy(vocabulary, 'term')
                        }

                        this.$set(this.unit, 'vocabulary', vocabulary)

                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },
        getUnitCrossword() {
            return new Promise((resolve, reject) => {
                this.axios.get('/api/units/crossword/' + this.unit.id)
                    .then(response => {
                        if (response.data) {
                            let crossword = null

                            if (response.data) {
                                crossword = response.data
                            }

                            if (crossword.vocab_data) {
                                crossword.vocab_data = _.orderBy(crossword.vocab_data, 'term')
                            }

                            if (
                                crossword.crossword_data &&
                                crossword.crossword_data.seed
                            ) {
                                this.$set(this.unit, 'crossword_seed', crossword.crossword_data.seed)
                            }

                            if (
                                this.unit &&
                                crossword
                            ) {
                                this.$set(this.unit, 'crossword', crossword)
                            }
                        }

                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },
        newVocabularyTerm() {
            this.show_vocabulary_term_modal = true
        },
        createVocabularyTerm(vocabulary_term) {
            this.axios.post('/api/articles/create_vocabulary/' + this.article.article_id, {
                vocabulary_term: vocabulary_term.term,
                definition: vocabulary_term.definition,
                sentence: vocabulary_term.sentence,
                image: vocabulary_term.image,
            })
                .then(() => {
                    this.getVocabularyTerms()

                    this.getUnitVocabularyTerms()
                    this.getUnitCrossword()

                    this.show_vocabulary_term_modal = false
                })
        },
        updateVocabularyTerm(vocabulary_term, existing_vocabulary_term) {
            let id = null

            if (
                existing_vocabulary_term &&
                existing_vocabulary_term.id
            ) {
                id = existing_vocabulary_term.id
            } else {
                id = vocabulary_term.vocabulary_term_id
            }

            this.axios.post('/api/articles/update_vocabulary/' + this.article.article_id, {
                vocabulary_term_id: id,
                vocabulary_term: vocabulary_term.term,
                definition: vocabulary_term.definition,
                image: vocabulary_term.image,
            })
                .then(response => {
                    if (response.data) {
                        this.getVocabularyTerms()

                        this.show_vocabulary_term_modal = false
                    }
                })
        },
        getNarration(question = null) {
            // walkthroughs don't have narration
            if (this.article.is_walkthrough) {
                return
            }

            let narrationTarget = question ? question : this.article
            this.$set(narrationTarget, 'narration_loading', true)

            let url = '/api/articles/narration/' + this.article.article_id + (question ? '/' + question.id : '')

            this.axios.get(url)
                .then(response => {
                    if (response.data) {
                        this.$set(narrationTarget, 'narration', response.data)
                    } else {
                        this.$set(narrationTarget, 'narration', {})
                    }

                    this.$set(narrationTarget, 'narration_loading', false)
                })
                .catch(error => {
                    if (error) {
                        this.$set(narrationTarget, 'narration', {})

                        this.$set(narrationTarget, 'narration_loading', false)
                    }
                })
        },
        updateNarrationStatus(narration, approved) {
            this.axios.post('/api/articles/update_narration_status/' + narration.id, {
                "approved": approved,
            })
                .then(response => {
                    if (response) {
                        this.getNarration()
                        narration.approved = approved === false ? null : 1;
                    }
                })
        },
        approveNarration(narration) {
            this.$swal({
                html: '<p>Are you sure you want to approve the narration audio/text sync for this article?</p><div class="text-warning">It is recommended that you verify that the audio is synced correctly first.</div>',
                showCancelButton: true,
                confirmButtonText: 'Approve Audio Sync',
                confirmButtonColor: '#27AE60',
                cancelButtonText: 'Cancel',
                reverseButtons: true
            }).then((result) => {
                if (result.value) {
                    this.updateNarrationStatus(narration, true)

                    this.show_narration_audio_sync_modal = false
                }
            })
        },
        reparseNarration(narration) {
            this.$swal({
                html: 'Are you sure you want to re-parse the text for this article?',
                showCancelButton: true,
                confirmButtonText: 'Re-parse Narration Text',
                confirmButtonColor: '#E67E22',
                cancelButtonText: 'Cancel',
                reverseButtons: true
            }).then((result) => {
                if (result.value) {
                    this.axios.post('/api/articles/parse_narration_text/' + narration.id)
                        .then(response => {
                            if (response) {
                                this.updateNarrationStatus(narration, false)

                                this.getNarration()
                            }
                        })
                }
            })
        },
        // Re-run alignment on audio we already have, through a named engine, so
        // the same recording can be compared across engines.
        async realignNarration(narration, driver, question = null) {
            const current = this.narration_driver_label(narration)
            const target = this.narration_driver_label({ alignment_driver: driver })

            const confirmation = await this.$swal({
                html: 'Re-align this narration with <strong>' + target + '</strong>?'
                    + '<div class="text-warning">It was aligned with ' + current
                    + '. The existing word timings are replaced once the new alignment lands.</div>',
                showCancelButton: true,
                confirmButtonText: 'Re-align with ' + target,
                confirmButtonColor: '#E67E22',
                cancelButtonText: 'Cancel',
                reverseButtons: true
            })
            if (!confirmation.value) {
                return
            }

            try {
                this.$Progress.start()
                const response = await this.axios.post(
                    '/api/articles/narration/' + narration.id + '/realign',
                    { alignment_driver: driver }
                )
                if (question) {
                    question.narration = response.data
                } else {
                    this.article.narration = response.data
                }
                this.getNarration(question)
                this.$Progress.finish()
            } catch (e) {
                this.$Progress.fail()
            }
        },
        testNarrationAudio(narration) {
            this.currentNarration = narration
            this.show_narration_audio_sync_modal = true

            if (this.$refs['narration-audio']) {
                this.$refs['narration-audio'].pause()
            }
        },
        deleteNarration(narration, question = null) {
            this.$swal({
                html: 'Are you sure you want to delete the narration for this article?',
                showCancelButton: true,
                confirmButtonText: 'Delete Narration',
                cancelButtonText: 'Cancel',
                reverseButtons: true
            }).then((result) => {
                if (result.value) {
                    this.axios.post('/api/articles/delete_narration/' + narration.id)
                        .then(response => {
                            if (response) {
                                this.getNarration()
                                if (question) {
                                    question.narration = {}
                                } else {
                                    this.article.narration = {}
                                }
                            }
                        })
                }
            })
        },
        newArticleQuestion(article) {
            if (!article.is_rubric && !article.is_walkthrough) {
                this.question = {
                    type: 'multiple_choice'
                }
            }

            this.show_question_modal = true
        },
        async createArticleQuestion(question) {
            try {
                this.$set(question, 'loading', true)

                await this.axios.post(`/api/articles/create_question/${this.article.article_id}`, {
                    type: question.type,
                    content: question.content,
                    print_view: question.print_view,
                    standards: question.standards,
                    unit_id: this.unit.id,
                    publication_id: this.publication.id,
                })
                await this.$store.dispatch(
                    'getLatestLogType',
                    {
                        article_id: this.article.id,
                        unit_id: this.unit.id,
                        publication_id: this.publication.id,
                    }
                )

                this.$set(question, 'loading', true)
                this.getArticle()
                this.show_question_modal = false
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to create question: ${e.message}`,
                    },
                    time: moment().toISOString(),
                })
            }
        },
        async updateArticleQuestion() {
            try {
                await this.$store.dispatch(
                    'getLatestLogType',
                    {
                        article_id: this.article.id,
                        unit_id: this.unit.id,
                        publication_id: this.publication.id,
                    }
                )
                this.getArticle()
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to update question: ${e.message}`,
                    },
                    time: moment().toISOString(),
                })
            }
        },
        async removeArticleQuestion() {
            try {
                await this.$store.dispatch(
                    'getLatestLogType',
                    {
                        article_id: this.article.id,
                        unit_id: this.unit.id,
                        publication_id: this.publication.id,
                    }
                )
                this.getArticle()
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to remove question: ${e.message}`,
                    },
                    time: moment().toISOString(),
                })
            }
        },
        uploadNarration(event, question = null) {
            const file = this.getNarrationUploadFile(event)
            if (!file) {
                return
            }

            this.$swal({
                html: '<p>Is this audio intended for a comic strip?</p><div class="text-warning">Comic strip audio will not be submitted for alignment.</div>',
                showCancelButton: true,
                confirmButtonText: 'Yes',
                confirmButtonColor: '#27AE60',
                cancelButtonText: 'No',
                focusCancel: true,
                reverseButtons: true
            }).then((result) => {
                let is_comic_audio = false;
                if (result.value) {
                    is_comic_audio = true;
                }

                let form_data = new FormData()
                form_data.append('audio', file, file.name)
                form_data.append('article_id', this.article.article_id)
                form_data.append('is_comic_audio', is_comic_audio ? 'true' : 'false')
                form_data.append('alignment_driver', this.alignment_driver)
                if (question && question.id) {
                    form_data.append('question_id', question.id)
                }
                this.uploading_file = true
                this.axios.post('/api/articles/create_narration/' + this.publication.id, form_data)
                    .then((response) => {
                        this.getNarration(question)
                        if (question) {
                            question.narration = response.data
                        } else {
                            this.article.narration = response.data
                        }
                        this.uploading_file = false
                        this.narration_file = null
                    })
                    .catch(error => {
                        if (error) {
                            this.uploading_file = false
                            this.narration_file = null
                        }
                    })
            })
        },
        getNarrationUploadFile(event) {
            if (event && event.target && event.target.files && event.target.files.length) {
                return event.target.files[0]
            }

            if (event && event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files.length) {
                return event.dataTransfer.files[0]
            }

            if (typeof FileList !== 'undefined' && event instanceof FileList && event.length) {
                return event[0]
            }

            if (typeof File !== 'undefined' && event instanceof File) {
                return event
            }

            if (typeof File !== 'undefined' && this.narration_file instanceof File) {
                return this.narration_file
            }

            return null
        },
        addTag(tag) {
            this.axios.post('/api/articles/add_tag/' + this.article.article_id, {
                tag_id: tag.id
            })
        },
        removeTag(tag) {
            this.axios.post('/api/articles/remove_tag/' + this.article.article_id, {
                tag_id: tag.id
            })
        },
        setContent(field, value) {
            if (value) {
                this.$set(this.article_data, field, value)
            }
        },
        toggleVisibility(value) {
            if (this.article[value]) {
                this.$set(this.article, value, false)
            } else {
                this.$set(this.article, value, true)
            }
        },
        publicationIsPreK() {
            let isPreK = false;
            _.forEach(this.publication.state_grades, function (value) {
                if (value.grade == "-1") {
                    isPreK = true;
                }
            });
            return isPreK;
        },
        async toggleHideMissingAudioMessage(event) {
            try {
                await this.axios.post('/api/articles/update/' + this.article.article_id, {
                    publication_id: this.publication.id,
                    unit_id: this.unit.id,
                    hide_missing_audio_message: event
                })
            } catch (e) {
                this.hide_missing_audio_message = !this.hide_missing_audio_message
            }
        },
        updateCustomArticleNumber(article_id, article_custom_number) {
            this.axios.post('/api/articles/update_custom_article_number/' + this.unit.id + '/' + article_id + '/' + article_custom_number, {})
                .then(response => {
                    if (response.data && response.data == 1) {
                        this.$emit('updatedCustomNumber');
                    }
                })
                .catch(error => {
                    if (error) {

                    }
                })
        },
        customNumberConflict(custom_number) {
            let custom_numbers = _.map(this.unit.articles, 'custom_number');
            let appearances = 0;

            _.forEach(this.unit.articles, other_articles => {
                if (custom_number == other_articles.custom_number && other_articles.page != "Extended Reading" && other_articles.include_numbering && other_articles.body) {
                    appearances++;
                }
            })

            if (appearances > 1) {
                return true;
            }
            return false;
        },
        reorderArticle(updated_article, direction, position_in_list) {
            if (this.updating_order) {
                return false;
            }
            this.updating_order = true;
            let last = null;
            let found = false;
            let moved = null;
            let prev = null;
            let following = null;
            let order_changes = [];
            let all_following = [];

            _.each(this.unit.articles, article => {
                //Article ids match
                if (article.unit_page_slot_id == updated_article.unit_page_slot_id) {
                    prev = last;
                    moved = article;
                    found = true;
                } else {
                    if (found && following && article.page == following.page) {
                        all_following.push(article);
                    }
                    if (found && !following) {
                        following = article;
                    }

                    if (article.id) {
                        last = article;
                    }
                }
            })

            let new_order_number;
            if (direction == 'up') {
                if (prev) {
                    new_order_number = prev.order_number + 1;
                    if (prev.page == moved.page) {
                        order_changes.push({
                            'unit_page_slot_id': moved.unit_page_slot_id,
                            'order_number': prev.order_number,
                            'page': prev.page
                        });
                        order_changes.push({
                            'unit_page_slot_id': prev.unit_page_slot_id,
                            'order_number': prev.order_number + 1,
                            'page': prev.page
                        });
                    } else if (moved.page == '2 & 3') {
                        order_changes.push({
                            'unit_page_slot_id': moved.unit_page_slot_id,
                            'order_number': new_order_number,
                            'page': '1'
                        });
                    } else if (moved.page == '4') {
                        order_changes.push({
                            'unit_page_slot_id': moved.unit_page_slot_id,
                            'order_number': new_order_number,
                            'page': '2 & 3'
                        });
                    } else if (moved.page == 'Extended Reading') {
                        order_changes.push({
                            'unit_page_slot_id': moved.unit_page_slot_id,
                            'order_number': new_order_number,
                            'page': '4'
                        });
                    }
                } else {
                    new_order_number = 1;
                    if (moved.page == '2 & 3') {
                        order_changes.push({
                            'unit_page_slot_id': moved.unit_page_slot_id,
                            'order_number': new_order_number,
                            'page': '1'
                        });
                    } else if (moved.page == '4') {
                        order_changes.push({
                            'unit_page_slot_id': moved.unit_page_slot_id,
                            'order_number': new_order_number,
                            'page': '2 & 3'
                        });
                    } else if (moved.page == 'Extended Reading') {
                        order_changes.push({
                            'unit_page_slot_id': moved.unit_page_slot_id,
                            'order_number': new_order_number,
                            'page': '4'
                        });
                    }
                }
            } else {
                let current_order;
                let new_page = moved.page;
                if (!following) {//article is being moved to an empty page
                    if (moved.page == '1') {
                        new_page = '2 & 3'
                    } else if (moved.page == '2 & 3') {
                        new_page = '4';
                    } else {
                        new_page = 'Extended Reading';
                    }
                    order_changes.push({
                        'unit_page_slot_id': moved.unit_page_slot_id,
                        'order_number': 1,
                        'page': new_page
                    });
                } else if (following && moved.page == following.page) {// article has precedding and following articles and is staying on the same page
                    current_order = following.order_number + 1;
                    let first_time_through = true;
                    order_changes.push({
                        'unit_page_slot_id': moved.unit_page_slot_id,
                        'order_number': current_order,
                        'page': following.page
                    });
                    if (all_following.length > 0) {
                        _.each(all_following, following_article => {
                            current_order++;
                            order_changes.push({
                                'unit_page_slot_id': following_article.unit_page_slot_id,
                                'order_number': current_order,
                                'page': following_article.page
                            });
                        });
                    }
                } else if (following.page == moved.page) {// article is being moved down within the same page
                    current_order = moved.order_number + 1;
                    order_changes.push({
                        'unit_page_slot_id': moved.unit_page_slot_id,
                        'order_number': current_order,
                        'page': following.page
                    });
                } else if (following.page != moved.page) {//article is moving down to new page
                    current_order = 1;
                    if (moved.page == '1') {
                        new_page = '2 & 3'
                    } else if (moved.page == '2 & 3') {
                        new_page = '4';
                    } else {
                        new_page = 'Extended Reading';
                    }
                    order_changes.push({
                        'unit_page_slot_id': moved.unit_page_slot_id,
                        'order_number': current_order,
                        'page': new_page
                    });
                    // Insert the moved article at the top of the destination page.
                    current_order++;
                    // Shift the existing destination-page articles down to preserve unique order numbers.
                    order_changes.push({
                        'unit_page_slot_id': following.unit_page_slot_id,
                        'order_number': current_order,
                        'page': following.page
                    });
                    if (all_following.length > 0) {
                        _.each(all_following, following_article => {
                            current_order++;
                            order_changes.push({
                                'unit_page_slot_id': following_article.unit_page_slot_id,
                                'order_number': current_order,
                                'page': following_article.page
                            });
                        });
                    }
                }
            }

            this.axios.post('/api/articles/update_article_order/', {
                order_changes: order_changes
            })
                .then(response => {
                    this.$emit('updatedOrderNumber');
                    this.updating_order = false;
                })

        },
        setArticleType() {
            if (this.article.is_activity) {
                this.article_type = 'activity'
            } else if (this.article.is_walkthrough) {
                this.article_type = 'walkthrough'
            } else if (this.article.is_rubric) {
                this.article_type = 'rubric'
            } else if (this.article.is_assessment) {
                this.article_type = 'assessment'
            } else {
                this.article_type = 'article'
            }
        },
        async updateArticleType(new_type) {
            if (this.article.is_assessment && new_type !== 'assessment') {
                const result = await this.$swal({
                    html: 'Are you sure you want to change this assessment to another article type?<br><br><span class="text-warning">If any student users have started working on this Assessment it will cause the Student Submission to no longer appear on SWO</span>',
                    showCancelButton: true,
                    confirmButtonText: 'Change Article Type',
                    confirmButtonColor: '#E67E22',
                    cancelButtonText: 'Cancel',
                    reverseButtons: true
                })

                if (!result.value) {
                    this.article.article_type = 'assessment'
                    this.article_type = 'assessment'

                    return false
                }
            }

            if (new_type === 'article' && (this.article.is_activity || this.article.is_assessment)) {
                const quesions_acceptable = await this.checkStandardArticleQuestions()
                if (!quesions_acceptable) {
                    this.article.article_type = this.article.is_assessment ? 'assessment' : 'activity'

                    return false
                }
            }

            const response = await this.axios.post(
                `/api/articles/update_article_type/${this.article.article_id}`,
                {
                    article_id: this.article.article_id,
                    new_type: new_type
                }
            )
            if (response.data) {
                // re-fetch the latest change-log entry because it can be type-specific
                await this.$store.dispatch(
                    'getLatestLogType',
                    {
                        article_id: this.article.id,
                        unit_id: this.unit.id,
                        publication_id: this.publication.id,
                    }
                )

                this.article.is_activity = response.data.is_activity;
                this.article.is_walkthrough = response.data.is_walkthrough;
                this.article.is_rubric = response.data.is_rubric;
                this.article.is_assessment = response.data.is_assessment;
                if (new_type == 'walkthrough') {
                    await this.toggleIncludeNumbering(false, this.unit.id, this.article.article_id);
                    this.include_numbering = false;
                }
                if (new_type == 'rubric') {
                    this.getRubric();
                }
            }
            this.getArticle()
            this.article_type = new_type;
        },
        getRubric() {
            if (!this.article.is_rubric) {
                this.tableData = [];
            } else {
                this.axios.get('/api/articles/rubric/' + this.article.article_id)
                    .then(response => {
                        if (response.data && response.data.id) {
                            this.tableData = response.data.rubric_data;
                            this.rubric_id = response.data.id;
                            this.rubric_type = response.data.type == 'single' ? 'Single Point Rubric' : 'Multi-Point Rubric';
                            this.rubric_updated = true;
                        } else {
                            this.createRubricTable();
                        }
                    })
                    .catch(error => {
                        if (error) {
                            if (
                                error.response &&
                                error.response.status &&
                                error.response.status == 401
                            ) {
                                //this.$set(this.unit, 'vocabulary_visible', false)
                            }

                            //this.$set(this.article, 'vocabulary_loading', false)
                        }
                    })
            }

        },
        handleRubricTypeChange(newValue) {
            if (this.rubric_updated) {
                this.$swal({
                    html: 'Changing rubric type will delete existing rubric data. Are you sure you want to change rubric type?',
                    showCancelButton: true,
                    confirmButtonText: 'Change Rubric Type',
                    cancelButtonText: 'Cancel',
                    reverseButtons: true,
                }).then((result) => {
                    if (result.value) {
                        this.createRubricTable();
                    } else {
                        // If the user cancels, set the select element value back to the previous value
                        let reverted_rubric_type = 'Multi-Point Rubric'
                        if (this.rubric_type === 'Multi-Point Rubric') {
                            reverted_rubric_type = 'Single Point Rubric'
                        }
                        this.rubric_type = reverted_rubric_type

                    }
                });
            } else {
                this.createRubricTable();
            }
        },
        createRubricTable() {
            if (this.rubric_type == 'Single Point Rubric') {
                this.tableData = [
                    {
                        index: 0,
                        row_type: 'header',
                        cells: [
                            {index: 0, text: 'Rubric Criteria'},
                        ]
                    },
                    {
                        index: 1,
                        row_type: 'data',
                        score: 1,
                        cells: [
                            {index: 0, text: ''},
                        ]
                    }
                ];
            } else {
                this.tableData = [
                    {
                        index: 0,
                        row_type: 'header',
                        cells: [
                            {index: 0, text: 'Rubric Criteria'},
                            {index: 1, text: '1'},
                        ]
                    },
                    {
                        index: 1,
                        row_type: 'data',
                        cells: [
                            {index: 0, text: ''},
                            {index: 1, text: ''},
                        ]
                    }
                ];
            }
            this.rubric_updated = false
        },
        remove_criteria_cell(cell_index) {
            if (this.tableData[0].cells.length <= 2) {
                this.$swal('Rubric must have at least one column')
                return
            }
            this.updating_rubric = true;
            let new_table = [];
            let new_index = 0;

            // build new table data
            for (let i = 0; i < this.tableData.length; i++) {
                new_table.push({index: i, cells: []});
                for (let j = 0; j < (this.tableData[0].cells.length); j++) {
                    if (j != cell_index) {
                        new_table[i].cells.push({index: new_index, text: this.tableData[i].cells[j].text});
                        new_index++;
                    }

                }
                new_index = 0;
            }

            for (let k = 0; k < new_table[0].cells.length; k++) {
                if (k !== 0) {
                    new_table[0].cells[k].text = new_table[0].cells.length - k
                }
            }

            this.tableData = new_table;
            this.saveRubric();
            this.updating_rubric = false;

        },
        addRubricColumn() {
            this.updating_rubric = true;
            let new_table = [];
            let new_index = 0;

            // build new table data
            for (let i = 0; i < this.tableData.length; i++) {
                new_table.push({index: i, cells: []});
                for (let j = 0; j < (this.tableData[0].cells.length); j++) {

                    new_table[i].cells.push({index: new_index, text: this.tableData[i].cells[j].text});
                    new_index++;

                    if (j === this.tableData[0].cells.length - 1) {
                        if (i === 0) {
                            new_table[i].cells.push({index: new_index, text: "1"});
                        } else {
                            new_table[i].cells.push({index: new_index, text: ""});
                        }
                        new_index++;
                    }
                }
                new_index = 0;
            }

            for (let k = 0; k < new_table[0].cells.length - 1; k++) {
                if (k != 0) {
                    new_table[0].cells[k].text = new_table[0].cells.length - (k);
                }

            }

            this.tableData = new_table;
            this.updating_rubric = false;
        },
        addRubricRow() {
            this.updating_rubric = true;
            let new_cells = [];

            this.tableData[this.tableData.length - 1].index = this.tableData[this.tableData.length - 1].index + 1;


            for (let i = 0; i < this.tableData[0].cells.length; i++) {
                new_cells.push({index: i, text: ""});
            }

            let new_row = {index: this.tableData.length - 1, cells: new_cells};

            if (this.rubric_type == 'Single Point Rubric') {
                this.$set(new_row, 'score', 1);
            }

            this.tableData.push(new_row);

            this.updating_rubric = false;

        },
        remove_criteria_row(remove_index) {
            if (this.tableData.length <= 2) {
                this.$swal('Rubric must have at least one row')
                return
            }
            this.updating_rubric = true;
            let new_index = 0;
            let new_table = [];

            for (let i = 0; i < this.tableData.length; i++) {
                if (i != remove_index) {
                    let new_row = {index: new_index, cells: this.tableData[i].cells, score: this.tableData[i].score};
                    new_table.push(new_row);
                    new_index++;
                }
            }
            this.tableData = new_table;
            this.saveRubric();
            this.updating_rubric = false;

        },
        handleSaveButton() {
            this.saving_rubric = true;
            this.saveRubric();
            setTimeout(() => {
                this.saving_rubric = false;
            }, 1500);
        },
        saveRubric() {
            this.rubric_updated = true;

            this.axios.post(`/api/articles/save_rubric/${this.article.id}`, {
                rubric_table: this.tableData,
                rubric_id: this.rubric_id,
                rubric_type: this.rubric_type
            }).then((response) => {
                if (response.data && response.data[0].id) {
                    this.rubric_id = response.data[0].id;

                }
            })
                .catch(error => {
                    if (error) {

                    }
                });

        },
        copyToClipboard(unit_id, article_id) {
            const baseURL = window.location.origin + window.location.pathname;
            copy(`${baseURL}?unit_id=${unit_id}&article_id=${article_id}`, {
                asHtml: false
            })

            this.copying_to_clipboard = true

            setTimeout(() => {
                this.copying_to_clipboard = false
            }, 1500)
        },


        async updateArticleFromTiptap(event) {
            this.$set(this.article_data, 'body', event);
            await this.updateArticleByProperty('body', this.article_data.body);
        },
        getLinkedAndLockedPedigrees() {
            const pedigrees = Array.isArray(this.article && this.article.pedigrees)
                ? this.article.pedigrees
                : []

            return pedigrees.filter((pedigree) => {
                const isDifferentUnit = pedigree.unit_id !== this.unit.id
                const isLinkedArticle = pedigree.relationship_type === 'Linked Article'

                return isDifferentUnit && isLinkedArticle && pedigree.locked
            })
        },
        showLockedPublications() {
            const pedigrees = this.getLinkedAndLockedPedigrees().map((pedigree) => {
                const pedigreeUrl = `/publications/${pedigree.publication_id}?unit_id=${pedigree.unit_id}&article_id=${pedigree.id}`

                return `<li><a href="${pedigreeUrl}" target="_blank">${pedigree.sku} - ${pedigree.title}</a></li>`
            })
            const lockedPubsList = `<ul>${pedigrees}</ul>`

            this.$swal('Locked Publications', lockedPubsList)
        },
        async refreshQuestionLayout() {
            await this.$store.dispatch(
                'getLatestLogType',
                {
                    article_id: this.article.id,
                    unit_id: this.unit.id,
                    publication_id: this.publication.id,
                }
            )

            this.getArticle()
        },
        async questionsAsPlaintext() {
            if (this.article_type !== 'activity') {
                return ''
            }

            try {
                return exportQuestions(this.article_data.questions)
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Could not copy questions as plaintext: ${e.message}`,
                    },
                    time: moment().toISOString(),
                })

                return ''
            }
        },
        onExpirationDateInput(event) {
            const ymd = event.target.value
            const time =
                (this.article.expiration_date || '').split(' ')[1] || '00:00:00'
            this.article.expiration_date = ymd ? `${ymd} ${time}` : null
            this.updateArticleExpirationDate()
        },
        async updateArticleExpirationDate(){

            try {
                this.axios.post('/api/articles/update_expiration_date/' + this.article.article_id, { expiration_date: this.article.expiration_date })
                .then(response => {
                    if (response.data) {
                        this.$set(this.article_data, 'expiration_date', response.data.expiration_date)
                    }
                })
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to update article expiration date: ${e.message}`,
                    },
                    time: moment().toISOString(),
                })
            } 
        },
    },
    mounted() {
        this.$set(this.article, 'predigree_chart_visible', false)
        this.$set(this.article, 'vocabulary_visible', false)
        this.$set(this.article, 'worksheets_visible', false)
        this.$set(this.article, 'new_custom_number', this.article.custom_number)
        this.setArticleType();

        if (this.article.id && this.unit.id && this.publication.id) {
            this.$store.dispatch(
                'getLatestLogType',
                {
                    article_id: this.article.id,
                    unit_id: this.unit.id,
                    publication_id: this.publication.id,
                }
            )
        }

        this.getRubric();
    },
    created() {
        // bad code, probably need to refactor to use vuex store to track articles_visible -KO
        eventBus.$emit('article-created')
        eventBus.$on('article-visible', (article_id) => {
            if (this.article.id == article_id) {
                if (!this.article.visible) {
                    this.$set(this.article, 'visible', true)
                }
            }
        })
    }
}
</script>
